import React, { useState, useEffect, useRef } from 'react';

/**
 * Time.ir API integration for Persian calendar events and holidays
 */

const FALLBACK_URL = 'https://api.allorigins.win/get?url=';

/**
 * Fetch Persian calendar events for a specific month
 * @param {number} year - Persian year (e.g., 1404)
 * @param {number} month - Persian month (1-12)
 * @param {number} day - Day (0 for entire month)
 * @returns {Promise<Object>} Calendar data with events
 */
const fetchPersianCalendarEvents = async (year, month, day = 0) => {
  const originalUrl = `https://api.time.ir/v1/event/fa/events/calendar?year=${year}&month=${month}&day=${day}&base1=0&base2=1&base3=2`;
  try {
    // Use AllOrigins proxy
    const proxyUrl = `${FALLBACK_URL}${encodeURIComponent(originalUrl)}`;
    const response = await fetch(proxyUrl);
    if (!response.ok) {
      throw new Error(`Proxy error! status: ${response.status}`);
    }
    const proxyData = await response.json();
    // Decode HTML entities
    const decodedContent = proxyData.contents.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#39;/g, "'");
    return JSON.parse(decodedContent);
  } catch (error) {
    // console.error('API call failed:', error);
    throw error;
  }
};

/**
 * Transform API events to calendar-compatible format
 * @param {Array} events - Events from API
 * @returns {Array} Formatted events
 */
const transformEvents = events => {
  return events.map(event => {
    // Convert Jalali to Gregorian for date field
    const gDate = jalaliToGregorian$1(event.jalali_year, event.jalali_month, event.jalali_day);
    return {
      id: event.id,
      title: event.title,
      date: `${gDate.gy}-${String(gDate.gm).padStart(2, '0')}-${String(gDate.gd).padStart(2, '0')}`,
      isHoliday: event.is_holiday,
      isRecurring: event.reoccur,
      description: event.body ? event.body.replace(/<[^>]*>/g, '').substring(0, 200) : '',
      calendar: event.base === 0 ? 'persian' : event.base === 1 ? 'gregorian' : 'hijri',
      media: event.media,
      color: event.is_holiday ? '#ef4444' : '#10b981'
    };
  });
};
function jalaliToGregorian$1(jy, jm, jd) {
  let gy = jy <= 979 ? 621 : 1600;
  jy -= jy <= 979 ? 0 : 979;
  let days = 365 * jy + Math.floor(jy / 33) * 8 + Math.floor((jy % 33 + 3) / 4) + 78 + jd + (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  gy += 400 * Math.floor(days / 146097);
  days %= 146097;
  let leap = true;
  if (days >= 36525) {
    days--;
    gy += 100 * Math.floor(days / 36524);
    days %= 36524;
    if (days >= 365) days++;else leap = false;
  }
  gy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days >= 366) {
    leap = false;
    days--;
    gy += Math.floor(days / 365);
    days %= 365;
  }
  const sal_a = [0, 31, leap || gy % 100 !== 0 && gy % 4 === 0 || gy % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let gm = 0;
  for (gm = 0; gm < 13 && days >= sal_a[gm]; gm++) days -= sal_a[gm];
  const gd = days + 1;
  return {
    gy,
    gm,
    gd
  };
}

/**
 * Get holidays for a specific Persian month
 * @param {number} year - Persian year
 * @param {number} month - Persian month
 * @returns {Promise<Array>} Array of holidays
 */
const getHolidays = async (year, month) => {
  try {
    const data = await fetchPersianCalendarEvents(year, month);
    const holidays = data.data.event_list.filter(event => event.is_holiday);
    return transformEvents(holidays);
  } catch (error) {
    console.error('Error fetching holidays:', error);
    return [];
  }
};

/**
 * Check if a specific date is a holiday
 * @param {number} year - Persian year
 * @param {number} month - Persian month  
 * @param {number} day - Persian day
 * @returns {Promise<boolean>} True if holiday
 */
const isHoliday = async (year, month, day) => {
  try {
    const data = await fetchPersianCalendarEvents(year, month, day);
    const dayData = data.data.day_list.find(d => d.index_in_base1 === day);
    return dayData ? dayData.is_holiday : false;
  } catch (error) {
    console.error('Error checking holiday status:', error);
    return false;
  }
};

/**
 * React hook to fetch Persian holidays for calendar integration
 * @param {number} year - Persian year
 * @param {number} month - Persian month
 * @returns {Object} { holidays, loading, error }
 */
const usePersianHolidays = (year, month) => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!year || !month) return;
    const fetchHolidays = async () => {
      setLoading(true);
      setError(null);
      try {
        const holidayData = await getHolidays(year, month);

        // Transform to calendar event format
        const calendarHolidays = holidayData.map(holiday => ({
          id: `holiday-${holiday.id}`,
          title: holiday.title,
          date: holiday.date.replace(/\//g, '-'),
          // Convert to YYYY-MM-DD format
          isHoliday: true,
          isAllDay: true,
          color: '#ef4444',
          description: holiday.description,
          readOnly: true // Holidays cannot be edited
        }));
        setHolidays(calendarHolidays);
      } catch (err) {
        setError(err.message);
        setHolidays([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHolidays();
  }, [year, month]);
  return {
    holidays,
    loading,
    error
  };
};

const PERSIAN_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
const PERSIAN_WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const PERSIAN_WEEKDAYS_RTL = ['ج', 'پ', 'چ', 'س', 'د', 'ی', 'ش'];
const PERSIAN_DIGITS$4 = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const toPersianDigits$4 = str => {
  return str.toString().replace(/\d/g, digit => PERSIAN_DIGITS$4[parseInt(digit)]);
};
function PersianDateTimePicker({
  value,
  onChange,
  defaultValue = null,
  disabled = false,
  disabledHours = [],
  showTime = true,
  minuteStep = 1,
  theme = {},
  outputFormat = 'iso',
  showFooter = true,
  minDate = null,
  maxDate = null,
  enabledDates = null,
  disabledDates = null,
  disabledWeekDays = [],
  persianNumbers = false,
  rtlCalendar = true,
  showHolidays = false
}) {
  const defaultTheme = {
    primaryColor: '#1890ff',
    backgroundColor: '#ffffff',
    borderColor: '#d9d9d9',
    hoverColor: '#f0f0f0',
    selectedTextColor: '#ffffff',
    textColor: '#000000',
    circularDates: false,
    ...theme
  };
  const lightenColor = (color, percent = 80) => {
    const num = parseInt(color.replace('#', ''), 16);
    const r = Math.min(255, (num >> 16) + Math.round((255 - (num >> 16)) * percent / 100));
    const g = Math.min(255, (num >> 8 & 0x00FF) + Math.round((255 - (num >> 8 & 0x00FF)) * percent / 100));
    const b = Math.min(255, (num & 0x0000FF) + Math.round((255 - (num & 0x0000FF)) * percent / 100));
    return '#' + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
  };
  const [selectedDay, setSelectedDay] = useState(null);
  const [timeStr, setTimeStr] = useState("00:00");
  const [showCalendar, setShowCalendar] = useState(false);
  const [displayMonth, setDisplayMonth] = useState({
    year: 1404,
    month: 9
  });
  const [today, setToday] = useState(null);
  const [viewMode, setViewMode] = useState('day');
  const [yearPage, setYearPage] = useState(0);
  const wrapperRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);

  // Fetch holidays for current month
  const {
    holidays
  } = usePersianHolidays(showHolidays ? displayMonth.year : null, showHolidays ? displayMonth.month : null);
  useEffect(() => {
    const todayDate = new Date();
    const jToday = gregorianToJalali(todayDate.getFullYear(), todayDate.getMonth() + 1, todayDate.getDate());
    setToday({
      year: jToday.jy,
      month: jToday.jm,
      day: jToday.jd
    });
    setDisplayMonth({
      year: jToday.jy,
      month: jToday.jm
    });

    // Handle defaultValue
    if (defaultValue && !value) {
      let d;
      if (defaultValue === 'now') {
        d = new Date();
      } else if (typeof defaultValue === 'number') {
        d = new Date(defaultValue);
      } else if (typeof defaultValue === 'string' && defaultValue.includes('T')) {
        d = new Date(defaultValue);
      } else {
        return;
      }
      const jDate = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
      setSelectedDay({
        year: jDate.jy,
        month: jDate.jm,
        day: jDate.jd
      });
      setDisplayMonth({
        year: jDate.jy,
        month: jDate.jm
      });
      setTimeStr(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
      updateValue({
        year: jDate.jy,
        month: jDate.jm,
        day: jDate.jd
      }, `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    }
  }, []);
  const isHoliday = (year, month, day) => {
    if (!showHolidays) return false;

    // Check API holidays first
    const gDate = jalaliToGregorian(year, month, day);
    const dateStr = `${gDate.gy}-${String(gDate.gm).padStart(2, '0')}-${String(gDate.gd).padStart(2, '0')}`;
    const apiHoliday = holidays?.find(h => h.date.startsWith(dateStr));
    if (apiHoliday) return true;

    // Fallback to Friday check
    const jsDate = new Date(gDate.gy, gDate.gm - 1, gDate.gd);
    return jsDate.getDay() === 5;
  };
  useEffect(() => {
    if (value) {
      let d;
      if (typeof value === 'number') {
        d = new Date(value);
      } else if (typeof value === 'string' && value.includes('T')) {
        d = new Date(value);
      } else {
        return;
      }
      const jDate = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
      setSelectedDay({
        year: jDate.jy,
        month: jDate.jm,
        day: jDate.jd
      });
      setDisplayMonth({
        year: jDate.jy,
        month: jDate.jm
      });
      setTimeStr(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    }
  }, []);
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsClosing(true);
        setTimeout(() => {
          setShowCalendar(false);
          setIsClosing(false);
        }, 200);
      }
    };
    const handleScroll = event => {
      if (showCalendar && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsClosing(true);
        setTimeout(() => {
          setShowCalendar(false);
          setIsClosing(false);
        }, 200);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showCalendar]);
  useEffect(() => {
    if (selectedDay && timeStr) {
      updateValue(selectedDay, timeStr);
    }
  }, [outputFormat]);
  const isDateDisabled = (year, month, day) => {
    const gDate = jalaliToGregorian(year, month, day);
    const dateStr = `${gDate.gy}-${String(gDate.gm).padStart(2, '0')}-${String(gDate.gd).padStart(2, '0')}`;

    // Check enabledDates (whitelist)
    if (enabledDates && Array.isArray(enabledDates) && enabledDates.length > 0) {
      const isEnabled = enabledDates.some(d => {
        const enabledDate = typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
        return enabledDate === dateStr;
      });
      if (!isEnabled) return true;
    }

    // Check disabledDates (blacklist)
    if (disabledDates && Array.isArray(disabledDates) && disabledDates.length > 0) {
      const isDisabled = disabledDates.some(d => {
        const disabledDate = typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
        return disabledDate === dateStr;
      });
      if (isDisabled) return true;
    }

    // Check disabledWeekDays (0=Saturday, 6=Friday)
    if (disabledWeekDays && Array.isArray(disabledWeekDays) && disabledWeekDays.length > 0) {
      const jsDate = new Date(gDate.gy, gDate.gm - 1, gDate.gd);
      const weekDay = (jsDate.getDay() + 1) % 7; // Convert to Persian week (0=Sat, 6=Fri)
      if (disabledWeekDays.includes(weekDay)) return true;
    }
    if (minDate) {
      const minDateObj = new Date(minDate);
      const minDateStr = `${minDateObj.getFullYear()}-${String(minDateObj.getMonth() + 1).padStart(2, '0')}-${String(minDateObj.getDate()).padStart(2, '0')}`;
      if (dateStr < minDateStr) return true;
    }
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      const maxDateStr = `${maxDateObj.getFullYear()}-${String(maxDateObj.getMonth() + 1).padStart(2, '0')}-${String(maxDateObj.getDate()).padStart(2, '0')}`;
      if (dateStr > maxDateStr) return true;
    }
    return false;
  };
  const handleDateSelect = day => {
    if (isDateDisabled(displayMonth.year, displayMonth.month, day)) return;
    const newDate = {
      year: displayMonth.year,
      month: displayMonth.month,
      day
    };
    setSelectedDay(newDate);
    updateValue(newDate, timeStr);
    if (!showFooter) {
      setIsClosing(true);
      setTimeout(() => {
        setShowCalendar(false);
        setIsClosing(false);
      }, 200);
    }
  };
  const handleOk = () => {
    if (selectedDay) {
      updateValue(selectedDay, timeStr);
    }
    setIsClosing(true);
    setTimeout(() => {
      setShowCalendar(false);
      setIsClosing(false);
      setViewMode('day');
    }, 200);
  };
  const handleCancel = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowCalendar(false);
      setIsClosing(false);
    }, 200);
  };
  const handleTimeChange = (time, timeString) => {
    setTimeStr(timeString);
    if (selectedDay) {
      updateValue(selectedDay, timeString);
    } else if (today) {
      updateValue(today, timeString);
    }
  };
  const updateValue = (date, time, skipOnChange = false) => {
    if (!date || !time) return;
    const [h, m] = time.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    const gDate = jalaliToGregorian(date.year, date.month, date.day);
    const jsDate = new Date(gDate.gy, gDate.gm - 1, gDate.gd, h, m);
    let output;
    switch (outputFormat) {
      case 'timestamp':
        output = jsDate.getTime();
        break;
      case 'shamsi':
        output = `${date.year}/${String(date.month).padStart(2, '0')}/${String(date.day).padStart(2, '0')} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        break;
      case 'gregorian':
        output = `${gDate.gy}/${String(gDate.gm).padStart(2, '0')}/${String(gDate.gd).padStart(2, '0')} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        break;
      case 'hijri':
        const hijri = gregorianToHijri$1(gDate.gy, gDate.gm, gDate.gd);
        output = `${hijri.year}/${String(hijri.month).padStart(2, '0')}/${String(hijri.day).padStart(2, '0')} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        break;
      default:
        output = `${gDate.gy}-${String(gDate.gm).padStart(2, '0')}-${String(gDate.gd).padStart(2, '0')}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
    }
    if (!skipOnChange) onChange(output);
  };
  const getDaysInMonth = (year, month) => {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    return isLeapJalaliYear(year) ? 30 : 29;
  };
  const isLeapJalaliYear = year => {
    const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
    let jp = breaks[0];
    let jump = 0;
    for (let i = 1; i < breaks.length; i++) {
      const jm = breaks[i];
      jump = jm - jp;
      if (year < jm) break;
      jp = jm;
    }
    let n = year - jp;
    if (jump - n < 6) n = n - jump + Math.floor((jump + 4) / 33) * 33;
    let leapJ = ((n + 1) % 33 - 1) % 4;
    if (leapJ === -1) leapJ = 4;
    return leapJ === 0;
  };
  const getFirstDayOfMonth = (year, month) => {
    const gDate = jalaliToGregorian(year, month, 1);
    const d = new Date(gDate.gy, gDate.gm - 1, gDate.gd);
    return (d.getDay() + 1) % 7;
  };
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(displayMonth.year, displayMonth.month);
    const firstDay = getFirstDayOfMonth(displayMonth.year, displayMonth.month);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(/*#__PURE__*/React.createElement("div", {
        key: `empty-${i}`,
        style: {
          width: '32px',
          height: '32px'
        }
      }));
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDay && selectedDay.year === displayMonth.year && selectedDay.month === displayMonth.month && selectedDay.day === day;
      const isToday = today && today.year === displayMonth.year && today.month === displayMonth.month && today.day === day;
      const isDisabled = isDateDisabled(displayMonth.year, displayMonth.month, day);
      days.push(/*#__PURE__*/React.createElement("div", {
        key: day,
        onClick: () => handleDateSelect(day),
        style: {
          width: window.innerWidth <= 768 ? '36px' : '32px',
          height: window.innerWidth <= 768 ? '36px' : '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          borderRadius: defaultTheme.circularDates ? '50%' : '4px',
          border: `1px solid ${defaultTheme.borderColor}`,
          backgroundColor: isSelected ? defaultTheme.primaryColor : isToday ? lightenColor(defaultTheme.primaryColor, 80) : isHoliday(displayMonth.year, displayMonth.month, day) ? '#ffebee' : 'transparent',
          color: isDisabled ? '#ccc' : isSelected ? defaultTheme.selectedTextColor : isHoliday(displayMonth.year, displayMonth.month, day) ? '#d32f2f' : defaultTheme.textColor,
          fontWeight: isSelected ? 'bold' : 'normal',
          fontSize: window.innerWidth <= 768 ? '16px' : '14px',
          transition: 'all 0.2s',
          opacity: isDisabled ? 0.3 : 1
        },
        onMouseEnter: e => !isSelected && !isDisabled && (e.target.style.backgroundColor = defaultTheme.hoverColor),
        onMouseLeave: e => !isSelected && !isDisabled && (e.target.style.backgroundColor = isToday ? lightenColor(defaultTheme.primaryColor, 80) : isHoliday(displayMonth.year, displayMonth.month, day) ? '#ffebee' : 'transparent')
      }, persianNumbers ? toPersianDigits$4(day) : day));
    }
    return days;
  };
  const changeMonth = delta => {
    let newMonth = displayMonth.month + delta;
    let newYear = displayMonth.year;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setDisplayMonth({
      year: newYear,
      month: newMonth
    });
  };
  const renderMonthPicker = () => {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        padding: '8px'
      }
    }, PERSIAN_MONTHS.map((m, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      onClick: () => {
        setDisplayMonth({
          ...displayMonth,
          month: i + 1
        });
        setViewMode('day');
      },
      style: {
        padding: '12px',
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        background: displayMonth.month === i + 1 ? defaultTheme.primaryColor : 'transparent',
        color: displayMonth.month === i + 1 ? defaultTheme.selectedTextColor : defaultTheme.textColor,
        fontWeight: displayMonth.month === i + 1 ? 'bold' : 'normal',
        transition: 'all 0.2s'
      },
      onMouseEnter: e => displayMonth.month !== i + 1 && (e.target.style.background = defaultTheme.hoverColor),
      onMouseLeave: e => displayMonth.month !== i + 1 && (e.target.style.background = 'transparent')
    }, m)));
  };
  const renderYearPicker = () => {
    const startYear = displayMonth.year - 4 + yearPage * 12;
    const years = Array.from({
      length: 12
    }, (_, i) => startYear + i);
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px',
        marginBottom: '8px'
      }
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setYearPage(yearPage - 1),
      style: {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: '20px',
        color: defaultTheme.primaryColor
      }
    }, "\xAB"), /*#__PURE__*/React.createElement("span", {
      style: {
        color: defaultTheme.textColor,
        fontWeight: 'bold'
      }
    }, startYear, " - ", startYear + 11), /*#__PURE__*/React.createElement("button", {
      onClick: () => setYearPage(yearPage + 1),
      style: {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: '20px',
        color: defaultTheme.primaryColor
      }
    }, "\xBB")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        padding: '8px'
      }
    }, years.map(y => /*#__PURE__*/React.createElement("div", {
      key: y,
      onClick: () => {
        setDisplayMonth({
          ...displayMonth,
          year: y
        });
        setViewMode('day');
        setYearPage(0);
      },
      style: {
        padding: '12px',
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        background: displayMonth.year === y ? defaultTheme.primaryColor : 'transparent',
        color: displayMonth.year === y ? defaultTheme.selectedTextColor : defaultTheme.textColor,
        fontWeight: displayMonth.year === y ? 'bold' : 'normal',
        transition: 'all 0.2s'
      },
      onMouseEnter: e => displayMonth.year !== y && (e.target.style.background = defaultTheme.hoverColor),
      onMouseLeave: e => displayMonth.year !== y && (e.target.style.background = 'transparent')
    }, y))));
  };
  const goToToday = () => {
    if (today) {
      setDisplayMonth({
        year: today.year,
        month: today.month
      });
      setSelectedDay(today);
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setTimeStr(currentTime);
      updateValue(today, currentTime);
    }
  };
  const displayValue = selectedDay ? `${persianNumbers ? toPersianDigits$4(selectedDay.year) : selectedDay.year}/${persianNumbers ? toPersianDigits$4(String(selectedDay.month).padStart(2, '0')) : String(selectedDay.month).padStart(2, '0')}/${persianNumbers ? toPersianDigits$4(String(selectedDay.day).padStart(2, '0')) : String(selectedDay.day).padStart(2, '0')}${showTime ? ` ${persianNumbers ? toPersianDigits$4(timeStr) : timeStr}` : ''}` : '';
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: '8px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      position: 'relative'
    },
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement("input", {
    value: displayValue,
    onClick: () => !disabled && setShowCalendar(!showCalendar),
    placeholder: "\u062A\u0627\u0631\u06CC\u062E \u0631\u0627 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F",
    readOnly: true,
    disabled: disabled,
    style: {
      boxSizing: 'border-box',
      margin: 0,
      padding: '4px 11px',
      color: 'rgba(0, 0, 0, 0.88)',
      fontSize: 'inherit',
      lineHeight: 1.57,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
      width: '100%',
      textAlign: 'start',
      backgroundColor: 'transparent',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      outline: 0,
      transition: 'all 0.2s linear',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      direction: 'rtl'
    }
  }), showCalendar && /*#__PURE__*/React.createElement("div", {
    ref: el => {
      if (el && wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 400;
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          el.style.position = 'fixed';
          el.style.top = '50%';
          el.style.left = '50%';
          el.style.transform = 'translate(-50%, -50%)';
          el.style.width = 'calc(100vw - 32px)';
          el.style.maxWidth = '400px';
        } else {
          el.style.transform = 'none';
          if (spaceBelow >= dropdownHeight) {
            el.style.top = `${rect.bottom + 4}px`;
          } else {
            el.style.bottom = `${window.innerHeight - rect.top + 4}px`;
          }
          el.style.left = `${Math.max(16, Math.min(rect.left, window.innerWidth - (showTime ? 420 : 300) - 16))}px`;
          el.style.width = showTime ? '420px' : '300px';
        }
      }
    },
    style: {
      position: 'fixed',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      borderRadius: '8px',
      background: defaultTheme.backgroundColor,
      padding: window.innerWidth <= 768 ? '12px' : '16px',
      maxWidth: 'calc(100vw - 32px)',
      display: 'flex',
      flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
      gap: window.innerWidth <= 768 ? '12px' : '16px',
      animation: isClosing ? 'fadeOut 0.2s ease-in-out' : 'fadeIn 0.2s ease-in-out'
    }
  }, /*#__PURE__*/React.createElement("style", null, `
            @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
            @keyframes fadeOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-10px); } }
          `), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      gap: '8px'
    }
  }, viewMode === 'day' && /*#__PURE__*/React.createElement("button", {
    onClick: () => changeMonth(1),
    style: {
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '4px 8px',
      color: defaultTheme.primaryColor
    }
  }, "\xAB"), viewMode !== 'day' && /*#__PURE__*/React.createElement("div", {
    style: {
      width: '32px'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: '8px',
      flex: 1,
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => setViewMode('month'),
    style: {
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      color: defaultTheme.textColor,
      fontWeight: 'bold',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => {
      e.target.style.color = defaultTheme.primaryColor;
      e.target.style.textDecoration = 'underline';
    },
    onMouseLeave: e => {
      e.target.style.color = defaultTheme.textColor;
      e.target.style.textDecoration = 'none';
    }
  }, PERSIAN_MONTHS[displayMonth.month - 1]), /*#__PURE__*/React.createElement("button", {
    onClick: () => setViewMode('year'),
    style: {
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      color: defaultTheme.textColor,
      fontWeight: 'bold',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => {
      e.target.style.color = defaultTheme.primaryColor;
      e.target.style.textDecoration = 'underline';
    },
    onMouseLeave: e => {
      e.target.style.color = defaultTheme.textColor;
      e.target.style.textDecoration = 'none';
    }
  }, displayMonth.year)), viewMode === 'day' && /*#__PURE__*/React.createElement("button", {
    onClick: () => changeMonth(-1),
    style: {
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '4px 8px',
      color: defaultTheme.primaryColor
    }
  }, "\xBB"), viewMode !== 'day' && /*#__PURE__*/React.createElement("div", {
    style: {
      width: '32px'
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out'
    }
  }, viewMode === 'day' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '4px',
      marginBottom: '8px',
      direction: rtlCalendar ? 'ltr' : ''
    }
  }, (rtlCalendar ? PERSIAN_WEEKDAYS_RTL : PERSIAN_WEEKDAYS).map(day => /*#__PURE__*/React.createElement("div", {
    key: day,
    style: {
      width: window.innerWidth <= 768 ? '36px' : '32px',
      height: window.innerWidth <= 768 ? '36px' : '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '12px',
      color: defaultTheme.textColor
    }
  }, day))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '4px',
      marginBottom: '12px',
      direction: rtlCalendar ? 'rtl' : 'ltr'
    }
  }, renderCalendar())), viewMode === 'month' && /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out'
    }
  }, renderMonthPicker()), viewMode === 'year' && /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out'
    }
  }, renderYearPicker())), /*#__PURE__*/React.createElement("style", null, `@keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }`), showFooter && /*#__PURE__*/React.createElement("div", {
    style: {
      borderTop: `1px solid ${defaultTheme.borderColor}`,
      paddingTop: '12px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'space-between'
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: goToToday,
    style: {
      padding: '4px 12px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '4px',
      background: defaultTheme.backgroundColor,
      cursor: 'pointer',
      fontSize: '12px',
      color: defaultTheme.textColor
    }
  }, "\u0627\u0644\u0627\u0646"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: '8px'
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleCancel,
    style: {
      padding: '4px 12px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '4px',
      background: defaultTheme.backgroundColor,
      cursor: 'pointer',
      fontSize: '12px',
      color: defaultTheme.textColor
    }
  }, "\u0644\u063A\u0648"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleOk,
    style: {
      padding: '4px 12px',
      border: 'none',
      borderRadius: '4px',
      background: defaultTheme.primaryColor,
      color: defaultTheme.selectedTextColor,
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold'
    }
  }, "\u062A\u0627\u06CC\u06CC\u062F"))))), showTime && /*#__PURE__*/React.createElement("div", {
    style: {
      borderLeft: window.innerWidth <= 768 ? 'none' : `1px solid ${defaultTheme.borderColor}`,
      borderTop: window.innerWidth <= 768 ? `1px solid ${defaultTheme.borderColor}` : 'none',
      paddingLeft: window.innerWidth <= 768 ? '0' : '16px',
      paddingTop: window.innerWidth <= 768 ? '12px' : '0',
      display: 'flex',
      gap: '8px',
      minWidth: '120px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: '12px',
      color: defaultTheme.textColor,
      opacity: 0.6,
      textAlign: 'center',
      marginBottom: '8px'
    }
  }, "\u062F\u0642\u06CC\u0642\u0647"), /*#__PURE__*/React.createElement("div", {
    style: {
      maxHeight: '250px',
      overflowY: 'auto',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '4px'
    }
  }, Array.from({
    length: Math.floor(60 / minuteStep)
  }, (_, i) => i * minuteStep).map(m => /*#__PURE__*/React.createElement("div", {
    key: m,
    onClick: () => {
      const newTime = `${timeStr.split(':')[0]}:${String(m).padStart(2, '0')}`;
      handleTimeChange(null, newTime);
    },
    style: {
      padding: '6px 12px',
      cursor: 'pointer',
      fontSize: '14px',
      background: timeStr.split(':')[1] === String(m).padStart(2, '0') ? defaultTheme.primaryColor : defaultTheme.backgroundColor,
      color: timeStr.split(':')[1] === String(m).padStart(2, '0') ? defaultTheme.selectedTextColor : defaultTheme.textColor,
      textAlign: 'center'
    },
    onMouseEnter: e => timeStr.split(':')[1] !== String(m).padStart(2, '0') && (e.target.style.background = defaultTheme.hoverColor),
    onMouseLeave: e => timeStr.split(':')[1] !== String(m).padStart(2, '0') && (e.target.style.background = defaultTheme.backgroundColor)
  }, persianNumbers ? toPersianDigits$4(String(m).padStart(2, '0')) : String(m).padStart(2, '0'))))), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: '12px',
      color: defaultTheme.textColor,
      opacity: 0.6,
      textAlign: 'center',
      marginBottom: '8px'
    }
  }, "\u0633\u0627\u0639\u062A"), /*#__PURE__*/React.createElement("div", {
    style: {
      maxHeight: '250px',
      overflowY: 'auto',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '4px'
    }
  }, Array.from({
    length: 24
  }, (_, i) => i).filter(h => !disabledHours.includes(h)).map(h => /*#__PURE__*/React.createElement("div", {
    key: h,
    onClick: () => {
      const newTime = `${String(h).padStart(2, '0')}:${timeStr.split(':')[1] || '00'}`;
      handleTimeChange(null, newTime);
    },
    style: {
      padding: '6px 12px',
      cursor: 'pointer',
      fontSize: '14px',
      background: timeStr.split(':')[0] === String(h).padStart(2, '0') ? defaultTheme.primaryColor : defaultTheme.backgroundColor,
      color: timeStr.split(':')[0] === String(h).padStart(2, '0') ? defaultTheme.selectedTextColor : defaultTheme.textColor,
      textAlign: 'center'
    },
    onMouseEnter: e => timeStr.split(':')[0] !== String(h).padStart(2, '0') && (e.target.style.background = defaultTheme.hoverColor),
    onMouseLeave: e => timeStr.split(':')[0] !== String(h).padStart(2, '0') && (e.target.style.background = defaultTheme.backgroundColor)
  }, persianNumbers ? toPersianDigits$4(String(h).padStart(2, '0')) : String(h).padStart(2, '0')))))))));
}
function gregorianToJalali(gy, gm, gd) {
  const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy = gy <= 1600 ? 0 : 979;
  gy -= gy <= 1600 ? 621 : 1600;
  let gy2 = gm > 2 ? gy + 1 : gy;
  let days = 365 * gy + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) + Math.floor((gy2 + 399) / 400) - 80 + gd + g_d_m[gm - 1];
  jy += 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return {
    jy,
    jm,
    jd
  };
}
function jalaliToGregorian(jy, jm, jd) {
  let gy = jy <= 979 ? 621 : 1600;
  jy -= jy <= 979 ? 0 : 979;
  let days = 365 * jy + Math.floor(jy / 33) * 8 + Math.floor((jy % 33 + 3) / 4) + 78 + jd + (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  gy += 400 * Math.floor(days / 146097);
  days %= 146097;
  let leap = true;
  if (days >= 36525) {
    days--;
    gy += 100 * Math.floor(days / 36524);
    days %= 36524;
    if (days >= 365) days++;else leap = false;
  }
  gy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days >= 366) {
    leap = false;
    days--;
    gy += Math.floor(days / 365);
    days %= 365;
  }
  const sal_a = [0, 31, leap || gy % 100 !== 0 && gy % 4 === 0 || gy % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let gm = 0;
  for (gm = 0; gm < 13 && days >= sal_a[gm]; gm++) days -= sal_a[gm];
  const gd = days + 1;
  return {
    gy,
    gm,
    gd
  };
}
function gregorianToHijri$1(gy, gm, gd) {
  const jd = Math.floor(1461 * (gy + 4800 + Math.floor((gm - 14) / 12)) / 4) + Math.floor(367 * (gm - 2 - 12 * Math.floor((gm - 14) / 12)) / 12) - Math.floor(3 * Math.floor((gy + 4900 + Math.floor((gm - 14) / 12)) / 100) / 4) + gd - 32075;
  const l = jd - 1948440 + 10632;
  const n = Math.floor((l - 1) / 10631);
  const l2 = l - 10631 * n + 354;
  const j = Math.floor((10985 - l2) / 5316) * Math.floor(50 * l2 / 17719) + Math.floor(l2 / 5670) * Math.floor(43 * l2 / 15238);
  const l3 = l2 - Math.floor((30 - j) / 15) * Math.floor(17719 * j / 50) - Math.floor(j / 16) * Math.floor(15238 * j / 43) + 29;
  const month = Math.floor(24 * l3 / 709);
  const day = l3 - Math.floor(709 * month / 24);
  const year = 30 * n + j - 30;
  return {
    year,
    month,
    day
  };
}

const PERSIAN_DIGITS$3 = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const toPersianDigits$3 = str => str.toString().replace(/\d/g, digit => PERSIAN_DIGITS$3[parseInt(digit)]);
const PersianDateRangePicker = ({
  value,
  onChange,
  defaultValue = null,
  placeholder = ['تاریخ شروع', 'تاریخ پایان'],
  disabled = false,
  className = '',
  theme = {},
  outputFormat = 'iso',
  showFooter = true,
  minDate = null,
  maxDate = null,
  enabledDates = null,
  disabledDates = null,
  disabledWeekDays = [],
  persianNumbers = false,
  rtlCalendar = true,
  showHolidays = false
}) => {
  const defaultTheme = {
    primaryColor: '#1890ff',
    backgroundColor: '#ffffff',
    borderColor: '#d9d9d9',
    hoverColor: '#f0f0f0',
    selectedTextColor: '#ffffff',
    textColor: '#000000',
    circularDates: false,
    ...theme
  };
  const lightenColor = (color, percent = 80) => {
    const num = parseInt(color.replace('#', ''), 16);
    const r = Math.min(255, (num >> 16) + Math.round((255 - (num >> 16)) * percent / 100));
    const g = Math.min(255, (num >> 8 & 0x00FF) + Math.round((255 - (num >> 8 & 0x00FF)) * percent / 100));
    const b = Math.min(255, (num & 0x0000FF) + Math.round((255 - (num & 0x0000FF)) * percent / 100));
    return '#' + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
  };
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(value?.[0] || null);
  const [endDate, setEndDate] = useState(value?.[1] || null);
  const [hoverDate, setHoverDate] = useState(null);
  const [leftMonth, setLeftMonth] = useState(null);
  const [leftYear, setLeftYear] = useState(null);
  const [rightMonth, setRightMonth] = useState(null);
  const [rightYear, setRightYear] = useState(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [leftViewMode, setLeftViewMode] = useState('day');
  const [rightViewMode, setRightViewMode] = useState('day');
  const [leftYearPage, setLeftYearPage] = useState(0);
  const [rightYearPage, setRightYearPage] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  // Fetch holidays for both months
  const {
    holidays: leftHolidays
  } = usePersianHolidays(showHolidays ? leftYear : null, showHolidays ? leftMonth : null);
  const {
    holidays: rightHolidays
  } = usePersianHolidays(showHolidays ? rightYear : null, showHolidays ? rightMonth : null);
  useEffect(() => {
    if (value && Array.isArray(value) && value[0] && typeof value[0] === 'string' && value[0].includes('-')) {
      setStartDate(value[0]);
      setEndDate(value[1]);
    } else if (defaultValue && Array.isArray(defaultValue) && defaultValue.length === 2 && !value) {
      // Handle defaultValue
      let start, end;
      if (typeof defaultValue[0] === 'number') {
        start = new Date(defaultValue[0]).toISOString().split('T')[0];
        end = new Date(defaultValue[1]).toISOString().split('T')[0];
      } else if (typeof defaultValue[0] === 'string') {
        start = defaultValue[0].includes('T') ? defaultValue[0].split('T')[0] : defaultValue[0];
        end = defaultValue[1].includes('T') ? defaultValue[1].split('T')[0] : defaultValue[1];
      }
      if (start && end) {
        setStartDate(start);
        setEndDate(end);
        const output = formatOutput(start, end);
        onChange?.(output);
      }
    }
  }, []);
  useEffect(() => {
    const now = new Date();
    const jalali = gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());

    // Left calendar shows current month (earlier)
    setLeftMonth(jalali.month);
    setLeftYear(jalali.year);

    // Right calendar shows next month (later)
    let nextMonth = jalali.month + 1;
    let nextYear = jalali.year;
    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear++;
    }
    setRightMonth(nextMonth);
    setRightYear(nextYear);
  }, []);
  const isHoliday = (year, month, day) => {
    if (!showHolidays) return false;

    // Check API holidays first
    const gDate = jalaliToGregorian(year, month, day);
    const dateStr = `${gDate.year}-${String(gDate.month).padStart(2, '0')}-${String(gDate.day).padStart(2, '0')}`;

    // Check both left and right month holidays
    const allHolidays = [...(leftHolidays || []), ...(rightHolidays || [])];
    const apiHoliday = allHolidays.find(h => h.date.startsWith(dateStr));
    if (apiHoliday) return true;

    // Fallback to Friday check
    const jsDate = new Date(gDate.year, gDate.month - 1, gDate.day);
    return jsDate.getDay() === 5;
  };
  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsClosing(true);
        setTimeout(() => {
          setIsOpen(false);
          setIsClosing(false);
        }, 200);
      }
    };
    const handleScroll = e => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsClosing(true);
        setTimeout(() => {
          setIsOpen(false);
          setIsClosing(false);
        }, 200);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);
  useEffect(() => {
    if (startDate && endDate) {
      const output = formatOutput(startDate, endDate);
      onChange?.(output);
    }
  }, [outputFormat]);
  useEffect(() => {
    if (startDate && endDate) {
      const output = formatOutput(startDate, endDate);
      onChange?.(output);
    }
  }, [outputFormat]);
  const gregorianToJalali = (gy, gm, gd) => {
    const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    const jy = gy <= 1600 ? 0 : 979;
    gy -= gy <= 1600 ? 621 : 1600;
    let days = 365 * gy + Math.floor((gy + 3) / 4) - Math.floor((gy + 99) / 100) + Math.floor((gy + 399) / 400) - 80 + gd + g_d_m[gm - 1];
    if (gm > 2 && (gy % 4 === 0 && gy % 100 !== 0 || gy % 400 === 0)) days++;
    let jy_final = jy + 33 * Math.floor(days / 12053);
    days %= 12053;
    jy_final += 4 * Math.floor(days / 1461);
    days %= 1461;
    if (days > 365) {
      jy_final += Math.floor((days - 1) / 365);
      days = (days - 1) % 365;
    }
    const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
    const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
    return {
      year: jy_final,
      month: jm,
      day: jd
    };
  };
  const jalaliToGregorian = (jy, jm, jd) => {
    const gy = jy <= 979 ? 621 : 1600;
    jy -= jy <= 979 ? 0 : 979;
    let days = 365 * jy + Math.floor(jy / 33) * 8 + Math.floor((jy % 33 + 3) / 4) + 78 + jd + (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
    let gy_final = gy + 400 * Math.floor(days / 146097);
    days %= 146097;
    if (days > 36524) {
      gy_final += 100 * Math.floor(--days / 36524);
      days %= 36524;
      if (days >= 365) days++;
    }
    gy_final += 4 * Math.floor(days / 1461);
    days %= 1461;
    if (days > 365) {
      gy_final += Math.floor((days - 1) / 365);
      days = (days - 1) % 365;
    }
    const g_d_m = [0, 31, gy_final % 4 === 0 && gy_final % 100 !== 0 || gy_final % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let gm = 0;
    for (let i = 0; i < 13; i++) {
      const v = g_d_m[i];
      if (days < v) break;
      days -= v;
      gm++;
    }
    return {
      year: gy_final,
      month: gm,
      day: days + 1
    };
  };
  const persianMonths = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
  const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
  const weekDaysRTL = ['ج', 'پ', 'چ', 'س', 'د', 'ی', 'ش'];
  const getDaysInMonth = (year, month) => {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    const isLeap = (year - 979) % 33 % 4 === 1;
    return isLeap ? 30 : 29;
  };
  const getFirstDayOfMonth = (year, month) => {
    const greg = jalaliToGregorian(year, month, 1);
    const date = new Date(greg.year, greg.month - 1, greg.day);
    return (date.getDay() + 1) % 7;
  };
  const isDateDisabled = (year, month, day) => {
    const greg = jalaliToGregorian(year, month, day);
    const dateStr = `${greg.year}-${String(greg.month).padStart(2, '0')}-${String(greg.day).padStart(2, '0')}`;

    // Check enabledDates (whitelist)
    if (enabledDates && Array.isArray(enabledDates) && enabledDates.length > 0) {
      const isEnabled = enabledDates.some(d => {
        const enabledDate = typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
        return enabledDate === dateStr;
      });
      if (!isEnabled) return true;
    }

    // Check disabledDates (blacklist)
    if (disabledDates && Array.isArray(disabledDates) && disabledDates.length > 0) {
      const isDisabled = disabledDates.some(d => {
        const disabledDate = typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
        return disabledDate === dateStr;
      });
      if (isDisabled) return true;
    }

    // Check disabledWeekDays (0=Saturday, 6=Friday)
    if (disabledWeekDays && Array.isArray(disabledWeekDays) && disabledWeekDays.length > 0) {
      const jsDate = new Date(greg.year, greg.month - 1, greg.day);
      const weekDay = (jsDate.getDay() + 1) % 7; // Convert to Persian week (0=Sat, 6=Fri)
      if (disabledWeekDays.includes(weekDay)) return true;
    }
    if (minDate) {
      const minDateObj = new Date(minDate);
      const minDateStr = `${minDateObj.getFullYear()}-${String(minDateObj.getMonth() + 1).padStart(2, '0')}-${String(minDateObj.getDate()).padStart(2, '0')}`;
      if (dateStr < minDateStr) return true;
    }
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      const maxDateStr = `${maxDateObj.getFullYear()}-${String(maxDateObj.getMonth() + 1).padStart(2, '0')}-${String(maxDateObj.getDate()).padStart(2, '0')}`;
      if (dateStr > maxDateStr) return true;
    }
    return false;
  };
  const handleDateClick = (year, month, day) => {
    if (isDateDisabled(year, month, day)) return;
    const greg = jalaliToGregorian(year, month, day);
    const isoString = `${greg.year}-${String(greg.month).padStart(2, '0')}-${String(greg.day).padStart(2, '0')}`;
    if (!startDate || startDate && endDate) {
      setStartDate(isoString);
      setEndDate(null);
    } else {
      const start = new Date(startDate);
      const end = new Date(isoString);
      if (end < start) {
        setStartDate(isoString);
        setEndDate(null);
      } else {
        setEndDate(isoString);
        if (!showFooter) {
          const output = formatOutput(startDate, isoString);
          onChange?.(output);
          setIsClosing(true);
          setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            setLeftViewMode('day');
            setRightViewMode('day');
          }, 200);
        }
      }
    }
  };
  const formatOutput = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    switch (outputFormat) {
      case 'timestamp':
        return [startDate.getTime(), endDate.getTime()];
      case 'shamsi':
        const sStart = gregorianToJalali(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate());
        const sEnd = gregorianToJalali(endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate());
        return [`${sStart.year}/${String(sStart.month).padStart(2, '0')}/${String(sStart.day).padStart(2, '0')}`, `${sEnd.year}/${String(sEnd.month).padStart(2, '0')}/${String(sEnd.day).padStart(2, '0')}`];
      case 'gregorian':
        return [`${startDate.getFullYear()}/${String(startDate.getMonth() + 1).padStart(2, '0')}/${String(startDate.getDate()).padStart(2, '0')}`, `${endDate.getFullYear()}/${String(endDate.getMonth() + 1).padStart(2, '0')}/${String(endDate.getDate()).padStart(2, '0')}`];
      case 'hijri':
        const hStart = gregorianToHijri(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate());
        const hEnd = gregorianToHijri(endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate());
        return [`${hStart.year}/${String(hStart.month).padStart(2, '0')}/${String(hStart.day).padStart(2, '0')}`, `${hEnd.year}/${String(hEnd.month).padStart(2, '0')}/${String(hEnd.day).padStart(2, '0')}`];
      default:
        return [start, end];
    }
  };
  const handleOk = () => {
    if (startDate && endDate) {
      const output = formatOutput(startDate, endDate);
      onChange?.(output);
    }
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      setLeftViewMode('day');
      setRightViewMode('day');
    }, 200);
  };
  const handleCancel = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 200);
  };
  const formatDate = isoString => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const jalali = gregorianToJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
    return `${jalali.year}/${String(jalali.month).padStart(2, '0')}/${String(jalali.day).padStart(2, '0')}`;
  };
  const isDateInRange = (year, month, day) => {
    const greg = jalaliToGregorian(year, month, day);
    const current = new Date(greg.year, greg.month - 1, greg.day);
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : hoverDate ? new Date(hoverDate) : null;
    if (!startDate || !end) return false;
    return current > start && current < end;
  };
  const isDateSelected = (year, month, day) => {
    const greg = jalaliToGregorian(year, month, day);
    const isoString = `${greg.year}-${String(greg.month).padStart(2, '0')}-${String(greg.day).padStart(2, '0')}`;
    return isoString === startDate || isoString === endDate;
  };
  const isDateStart = (year, month, day) => {
    const greg = jalaliToGregorian(year, month, day);
    const isoString = `${greg.year}-${String(greg.month).padStart(2, '0')}-${String(greg.day).padStart(2, '0')}`;
    return isoString === startDate;
  };
  const isDateEnd = (year, month, day) => {
    const greg = jalaliToGregorian(year, month, day);
    const isoString = `${greg.year}-${String(greg.month).padStart(2, '0')}-${String(greg.day).padStart(2, '0')}`;
    return isoString === endDate || !endDate && isoString === hoverDate;
  };
  const renderCalendar = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(/*#__PURE__*/React.createElement("div", {
        key: `empty-${i}`,
        style: {
          width: '32px',
          height: '32px'
        }
      }));
    }
    for (let day = 1; day <= daysInMonth; day++) {
      isDateSelected(year, month, day);
      const isInRange = isDateInRange(year, month, day);
      const isStart = isDateStart(year, month, day);
      const isEnd = isDateEnd(year, month, day);
      const isHol = isHoliday(year, month, day);
      const isDisabled = isDateDisabled(year, month, day);
      const isMobile = window.innerWidth <= 768;
      const dayStyle = {
        width: isMobile ? '30px' : '32px',
        height: isMobile ? '30px' : '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${defaultTheme.borderColor}`,
        fontSize: isMobile ? '14px' : '14px',
        color: isHol && !isStart && !isEnd ? '#d32f2f' : defaultTheme.textColor,
        transition: 'all 0.2s',
        opacity: isDisabled ? 0.3 : 1
      };
      if (isStart || isEnd) {
        dayStyle.backgroundColor = defaultTheme.primaryColor;
        dayStyle.color = defaultTheme.selectedTextColor;
        dayStyle.fontWeight = 'bold';
        dayStyle.borderRadius = defaultTheme.circularDates ? '50%' : '4px';
      } else if (isInRange) {
        dayStyle.backgroundColor = lightenColor(defaultTheme.primaryColor, 80);
        dayStyle.borderRadius = defaultTheme.circularDates ? '50%' : '4px';
      } else {
        dayStyle.backgroundColor = isHol ? '#ffebee' : 'transparent';
        dayStyle.borderRadius = defaultTheme.circularDates ? '50%' : '4px';
      }
      days.push(/*#__PURE__*/React.createElement("div", {
        className: "range-day",
        key: day,
        style: dayStyle,
        onClick: () => handleDateClick(year, month, day),
        onMouseEnter: e => {
          if (!isDisabled) {
            if (startDate && !endDate) {
              const greg = jalaliToGregorian(year, month, day);
              setHoverDate(`${greg.year}-${String(greg.month).padStart(2, '0')}-${String(greg.day).padStart(2, '0')}`);
            }
            if (!isStart && !isEnd && !isInRange && !isDisabled) {
              e.target.style.backgroundColor = defaultTheme.hoverColor;
            }
          }
        },
        onMouseLeave: e => {
          if (!isStart && !isEnd && !isInRange && !isDisabled) {
            e.target.style.backgroundColor = 'transparent';
          }
        }
      }, persianNumbers ? toPersianDigits$3(day) : day));
    }
    return days;
  };
  const renderMonthPicker = (month, setMonth, setViewMode) => {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        padding: '8px',
        maxHeight: '300px',
        overflowY: 'auto'
      }
    }, persianMonths.map((m, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      onClick: () => {
        setMonth(i + 1);
        setViewMode('day');
      },
      style: {
        padding: '12px',
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        background: month === i + 1 ? defaultTheme.primaryColor : 'transparent',
        color: month === i + 1 ? defaultTheme.selectedTextColor : defaultTheme.textColor,
        fontWeight: month === i + 1 ? 'bold' : 'normal',
        transition: 'all 0.2s'
      },
      onMouseEnter: e => month !== i + 1 && (e.target.style.background = defaultTheme.hoverColor),
      onMouseLeave: e => month !== i + 1 && (e.target.style.background = 'transparent')
    }, m)));
  };
  const renderYearPicker = (year, setYear, yearPage, setYearPage, setViewMode) => {
    const startYear = year - 4 + yearPage * 12;
    const years = Array.from({
      length: 12
    }, (_, i) => startYear + i);
    return /*#__PURE__*/React.createElement("div", {
      style: {
        maxHeight: '300px',
        overflowY: 'auto'
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px',
        marginBottom: '8px'
      }
    }, /*#__PURE__*/React.createElement("button", {
      onClick: () => setYearPage(yearPage - 1),
      style: {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: '20px',
        color: defaultTheme.primaryColor
      }
    }, "\xAB"), /*#__PURE__*/React.createElement("span", {
      style: {
        color: defaultTheme.textColor,
        fontWeight: 'bold'
      }
    }, startYear, " - ", startYear + 11), /*#__PURE__*/React.createElement("button", {
      onClick: () => setYearPage(yearPage + 1),
      style: {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: '20px',
        color: defaultTheme.primaryColor
      }
    }, "\xBB")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        padding: '8px'
      }
    }, years.map(y => /*#__PURE__*/React.createElement("div", {
      key: y,
      onClick: () => {
        setYear(y);
        setViewMode('day');
        setYearPage(0);
      },
      style: {
        padding: '12px',
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        background: year === y ? defaultTheme.primaryColor : 'transparent',
        color: year === y ? defaultTheme.selectedTextColor : defaultTheme.textColor,
        fontWeight: year === y ? 'bold' : 'normal',
        transition: 'all 0.2s'
      },
      onMouseEnter: e => year !== y && (e.target.style.background = defaultTheme.hoverColor),
      onMouseLeave: e => year !== y && (e.target.style.background = 'transparent')
    }, y))));
  };
  const changeLeftMonth = delta => {
    let newMonth = leftMonth + delta;
    let newYear = leftYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setLeftMonth(newMonth);
    setLeftYear(newYear);
  };
  const changeRightMonth = delta => {
    let newMonth = rightMonth + delta;
    let newYear = rightYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setRightMonth(newMonth);
    setRightYear(newYear);
  };
  const displayValue = startDate && endDate ? `${persianNumbers ? toPersianDigits$3(formatDate(startDate)) : formatDate(startDate)} ~ ${persianNumbers ? toPersianDigits$3(formatDate(endDate)) : formatDate(endDate)}` : startDate ? persianNumbers ? toPersianDigits$3(formatDate(startDate)) : formatDate(startDate) : '';
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    style: {
      position: 'relative',
      width: '100%'
    },
    className: className
  }, /*#__PURE__*/React.createElement("input", {
    value: displayValue,
    onClick: () => !disabled && setIsOpen(!isOpen),
    placeholder: `${placeholder[0]} ~ ${placeholder[1]}`,
    readOnly: true,
    disabled: disabled,
    style: {
      width: '100%',
      padding: '4px 11px',
      border: '1px solid #d9d9d9',
      borderRadius: '6px',
      fontSize: '14px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      fontFamily: 'inherit',
      transition: 'all 0.2s',
      direction: 'rtl',
      boxSizing: 'border-box',
      lineHeight: '1.57'
    }
  }), isOpen && /*#__PURE__*/React.createElement("div", {
    ref: dropdownRef,
    onMouseLeave: () => setHoverDate(null),
    style: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      borderRadius: '8px',
      background: defaultTheme.backgroundColor,
      padding: window.innerWidth <= 768 ? '8px' : '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: window.innerWidth <= 768 ? '8px' : '16px',
      animation: isClosing ? 'fadeOut 0.2s ease-in-out' : 'fadeIn 0.2s ease-in-out',
      maxWidth: 'calc(100vw - 16px)',
      maxHeight: 'calc(100vh - 16px)',
      overflowY: 'auto'
    },
    onScroll: e => e.stopPropagation(),
    onWheel: e => e.stopPropagation(),
    onTouchMove: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement("style", null, `
            @keyframes fadeIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
            @keyframes fadeOut { from { opacity: 1; transform: translate(-50%, -50%) scale(1); } to { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } }
            @keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
            @media (max-width: 768px) {
              .range-calendars { flex-direction: column !important; }
              .range-calendar { min-width: 240px !important; max-width: 100% !important; }
              .range-day { width: 30px !important; height: 30px !important; font-size: 14px !important; }
              .range-weekday { width: 30px !important; height: 30px !important; font-size: 10px !important; }
            }
          `), /*#__PURE__*/React.createElement("div", {
    className: "range-calendars",
    style: {
      display: 'flex',
      gap: window.innerWidth <= 768 ? '8px' : '16px',
      direction: 'rtl'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "range-calendar",
    style: {
      minWidth: window.innerWidth <= 768 ? '240px' : '300px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      gap: '8px'
    }
  }, leftViewMode === 'day' && /*#__PURE__*/React.createElement("button", {
    onClick: () => changeLeftMonth(-1),
    style: {
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '4px 8px',
      color: defaultTheme.primaryColor
    }
  }, "\xAB"), leftViewMode !== 'day' && /*#__PURE__*/React.createElement("div", {
    style: {
      width: '32px'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: '8px',
      flex: 1,
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => setLeftViewMode('year'),
    style: {
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      color: defaultTheme.textColor,
      fontWeight: 'bold',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => {
      e.target.style.color = defaultTheme.primaryColor;
      e.target.style.textDecoration = 'underline';
    },
    onMouseLeave: e => {
      e.target.style.color = defaultTheme.textColor;
      e.target.style.textDecoration = 'none';
    }
  }, leftYear), /*#__PURE__*/React.createElement("button", {
    onClick: () => setLeftViewMode('month'),
    style: {
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      color: defaultTheme.textColor,
      fontWeight: 'bold',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => {
      e.target.style.color = defaultTheme.primaryColor;
      e.target.style.textDecoration = 'underline';
    },
    onMouseLeave: e => {
      e.target.style.color = defaultTheme.textColor;
      e.target.style.textDecoration = 'none';
    }
  }, persianMonths[leftMonth - 1])), leftViewMode === 'day' && /*#__PURE__*/React.createElement("button", {
    onClick: () => changeLeftMonth(1),
    style: {
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '4px 8px',
      color: defaultTheme.primaryColor
    }
  }, "\xBB"), leftViewMode !== 'day' && /*#__PURE__*/React.createElement("div", {
    style: {
      width: '32px'
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out',
      maxHeight: '350px',
      overflowY: 'auto'
    }
  }, leftViewMode === 'day' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '4px',
      marginBottom: '8px',
      direction: rtlCalendar ? 'ltr' : ''
    }
  }, (rtlCalendar ? weekDaysRTL : weekDays).map(day => /*#__PURE__*/React.createElement("div", {
    className: "range-weekday",
    key: day,
    style: {
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '12px',
      color: defaultTheme.textColor
    }
  }, day))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '4px',
      direction: rtlCalendar ? 'rtl' : 'ltr'
    }
  }, renderCalendar(leftYear, leftMonth))), leftViewMode === 'month' && /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out'
    }
  }, renderMonthPicker(leftMonth, setLeftMonth, setLeftViewMode)), leftViewMode === 'year' && /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out'
    }
  }, renderYearPicker(leftYear, setLeftYear, leftYearPage, setLeftYearPage, setLeftViewMode)))), /*#__PURE__*/React.createElement("div", {
    className: "range-calendar",
    style: {
      minWidth: window.innerWidth <= 768 ? '240px' : '300px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      gap: '8px'
    }
  }, rightViewMode === 'day' && /*#__PURE__*/React.createElement("button", {
    onClick: () => changeRightMonth(-1),
    style: {
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '4px 8px',
      color: defaultTheme.primaryColor
    }
  }, "\xAB"), rightViewMode !== 'day' && /*#__PURE__*/React.createElement("div", {
    style: {
      width: '32px'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: '8px',
      flex: 1,
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => setRightViewMode('year'),
    style: {
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      color: defaultTheme.textColor,
      fontWeight: 'bold',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => {
      e.target.style.color = defaultTheme.primaryColor;
      e.target.style.textDecoration = 'underline';
    },
    onMouseLeave: e => {
      e.target.style.color = defaultTheme.textColor;
      e.target.style.textDecoration = 'none';
    }
  }, rightYear), /*#__PURE__*/React.createElement("button", {
    onClick: () => setRightViewMode('month'),
    style: {
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      color: defaultTheme.textColor,
      fontWeight: 'bold',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => {
      e.target.style.color = defaultTheme.primaryColor;
      e.target.style.textDecoration = 'underline';
    },
    onMouseLeave: e => {
      e.target.style.color = defaultTheme.textColor;
      e.target.style.textDecoration = 'none';
    }
  }, persianMonths[rightMonth - 1])), rightViewMode === 'day' && /*#__PURE__*/React.createElement("button", {
    onClick: () => changeRightMonth(1),
    style: {
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '4px 8px',
      color: defaultTheme.primaryColor
    }
  }, "\xBB"), rightViewMode !== 'day' && /*#__PURE__*/React.createElement("div", {
    style: {
      width: '32px'
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out',
      maxHeight: '350px',
      overflowY: 'auto'
    }
  }, rightViewMode === 'day' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '4px',
      marginBottom: '8px',
      direction: rtlCalendar ? 'ltr' : ''
    }
  }, (rtlCalendar ? weekDaysRTL : weekDays).map(day => /*#__PURE__*/React.createElement("div", {
    className: "range-weekday",
    key: day,
    style: {
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '12px',
      color: defaultTheme.textColor
    }
  }, day))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '4px',
      direction: rtlCalendar ? 'rtl' : 'ltr'
    }
  }, renderCalendar(rightYear, rightMonth))), rightViewMode === 'month' && /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out'
    }
  }, renderMonthPicker(rightMonth, setRightMonth, setRightViewMode)), rightViewMode === 'year' && /*#__PURE__*/React.createElement("div", {
    style: {
      animation: 'slideIn 0.2s ease-in-out'
    }
  }, renderYearPicker(rightYear, setRightYear, rightYearPage, setRightYearPage, setRightViewMode))))), showFooter && /*#__PURE__*/React.createElement("div", {
    style: {
      borderTop: `1px solid ${defaultTheme.borderColor}`,
      paddingTop: '16px',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px'
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleCancel,
    style: {
      padding: '6px 16px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '4px',
      background: defaultTheme.backgroundColor,
      cursor: 'pointer',
      fontSize: '14px',
      color: defaultTheme.textColor
    }
  }, "\u0644\u063A\u0648"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleOk,
    style: {
      padding: '6px 16px',
      border: 'none',
      borderRadius: '4px',
      background: defaultTheme.primaryColor,
      color: defaultTheme.selectedTextColor,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold'
    }
  }, "\u062A\u0627\u06CC\u06CC\u062F"))));
};
function gregorianToHijri(gy, gm, gd) {
  const jd = Math.floor(1461 * (gy + 4800 + Math.floor((gm - 14) / 12)) / 4) + Math.floor(367 * (gm - 2 - 12 * Math.floor((gm - 14) / 12)) / 12) - Math.floor(3 * Math.floor((gy + 4900 + Math.floor((gm - 14) / 12)) / 100) / 4) + gd - 32075;
  const l = jd - 1948440 + 10632;
  const n = Math.floor((l - 1) / 10631);
  const l2 = l - 10631 * n + 354;
  const j = Math.floor((10985 - l2) / 5316) * Math.floor(50 * l2 / 17719) + Math.floor(l2 / 5670) * Math.floor(43 * l2 / 15238);
  const l3 = l2 - Math.floor((30 - j) / 15) * Math.floor(17719 * j / 50) - Math.floor(j / 16) * Math.floor(15238 * j / 43) + 29;
  const month = Math.floor(24 * l3 / 709);
  const day = l3 - Math.floor(709 * month / 24);
  const year = 30 * n + j - 30;
  return {
    year,
    month,
    day
  };
}

const PERSIAN_DIGITS$2 = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const toPersianDigits$2 = str => str.toString().replace(/\d/g, digit => PERSIAN_DIGITS$2[parseInt(digit)]);
const PersianTimePicker = ({
  value = '',
  onChange,
  theme = {},
  minuteStep = 1,
  disabledHours = [],
  placeholder = 'انتخاب زمان',
  defaultValue = null,
  disabled = false,
  persianNumbers = false,
  isRange = false
}) => {
  const defaultTheme = {
    primaryColor: '#1890ff',
    backgroundColor: '#ffffff',
    borderColor: '#e8e8e8',
    textColor: '#000000',
    ...theme
  };
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);
  const getInitialTime = () => {
    if (isRange && Array.isArray(value) && value[0]) {
      return {
        startHour: parseInt(value[0].split(':')[0]),
        startMinute: parseInt(value[0].split(':')[1]),
        endHour: value[1] ? parseInt(value[1].split(':')[0]) : 0,
        endMinute: value[1] ? parseInt(value[1].split(':')[1]) : 0
      };
    }
    if (isRange && defaultValue) {
      if (defaultValue === 'now') {
        const now = new Date();
        const endHour = (now.getHours() + 1) % 24;
        return {
          startHour: now.getHours(),
          startMinute: now.getMinutes(),
          endHour: endHour,
          endMinute: now.getMinutes()
        };
      }
      if (Array.isArray(defaultValue) && defaultValue.length === 2) {
        return {
          startHour: parseInt(defaultValue[0].split(':')[0]),
          startMinute: parseInt(defaultValue[0].split(':')[1]),
          endHour: parseInt(defaultValue[1].split(':')[0]),
          endMinute: parseInt(defaultValue[1].split(':')[1])
        };
      }
    }
    if (value && !isRange) return {
      hour: parseInt(value.split(':')[0]),
      minute: parseInt(value.split(':')[1])
    };
    if (defaultValue === 'now' && !isRange) {
      const now = new Date();
      return {
        hour: now.getHours(),
        minute: now.getMinutes()
      };
    }
    if (defaultValue && typeof defaultValue === 'string' && defaultValue.includes(':') && !isRange) {
      return {
        hour: parseInt(defaultValue.split(':')[0]),
        minute: parseInt(defaultValue.split(':')[1])
      };
    }
    return isRange ? {
      startHour: 0,
      startMinute: 0,
      endHour: 0,
      endMinute: 0
    } : {
      hour: 0,
      minute: 0
    };
  };
  const initialTime = getInitialTime();
  const [selectedHour, setSelectedHour] = useState(initialTime.hour || 0);
  const [selectedMinute, setSelectedMinute] = useState(initialTime.minute || 0);
  const [startHour, setStartHour] = useState(initialTime.startHour || 0);
  const [startMinute, setStartMinute] = useState(initialTime.startMinute || 0);
  const [endHour, setEndHour] = useState(initialTime.endHour || 0);
  const [endMinute, setEndMinute] = useState(initialTime.endMinute || 0);
  const dropdownRef = useRef(null);
  useEffect(() => {
    // Set defaultValue when it changes
    if (defaultValue) {
      if (isRange) {
        if (defaultValue === 'now') {
          const now = new Date();
          const endHour = (now.getHours() + 1) % 24;
          const startTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
          const endTime = `${String(endHour).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
          setStartHour(now.getHours());
          setStartMinute(now.getMinutes());
          setEndHour(endHour);
          setEndMinute(now.getMinutes());
          onChange?.([startTime, endTime]);
        } else if (Array.isArray(defaultValue) && defaultValue.length === 2) {
          const sh = parseInt(defaultValue[0].split(':')[0]);
          const sm = parseInt(defaultValue[0].split(':')[1]);
          const eh = parseInt(defaultValue[1].split(':')[0]);
          const em = parseInt(defaultValue[1].split(':')[1]);
          setStartHour(sh);
          setStartMinute(sm);
          setEndHour(eh);
          setEndMinute(em);
          onChange?.(defaultValue);
        }
      } else {
        if (defaultValue === 'now') {
          const now = new Date();
          const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
          setSelectedHour(now.getHours());
          setSelectedMinute(now.getMinutes());
          onChange?.(timeStr);
        } else if (typeof defaultValue === 'string' && defaultValue.includes(':')) {
          const h = parseInt(defaultValue.split(':')[0]);
          const m = parseInt(defaultValue.split(':')[1]);
          setSelectedHour(h);
          setSelectedMinute(m);
          onChange?.(defaultValue);
        }
      }
    }
  }, [defaultValue, isRange]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const handleScroll = e => {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showDropdown]);
  const hours = Array.from({
    length: 24
  }, (_, i) => i).filter(h => !disabledHours.includes(h));
  const minutes = Array.from({
    length: 60 / minuteStep
  }, (_, i) => i * minuteStep);
  const handleTimeSelect = (hour, minute) => {
    if (isRange) {
      if (selectingStart) {
        setStartHour(hour);
        setStartMinute(minute);
        setSelectingStart(false);
      } else {
        setEndHour(hour);
        setEndMinute(minute);
        const startTime = `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`;
        const endTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        onChange?.([startTime, endTime]);
        setShowDropdown(false);
        setSelectingStart(true);
      }
    } else {
      setSelectedHour(hour);
      setSelectedMinute(minute);
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      onChange?.(timeStr);
      setShowDropdown(false);
    }
  };
  const displayValue = isRange && Array.isArray(value) && value[0] ? `${value[0]} ~ ${value[1] || ''}` : value || '';
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      width: '100%'
    },
    ref: dropdownRef
  }, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: displayValue,
    placeholder: isRange ? 'از ~ تا' : placeholder,
    readOnly: isRange,
    disabled: disabled,
    onChange: e => {
      if (isRange || disabled) return;
      const val = e.target.value;
      if (/^\d{0,2}:?\d{0,2}$/.test(val)) {
        if (val.includes(':') && val.length === 5) {
          const [h, m] = val.split(':');
          if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
            setSelectedHour(parseInt(h));
            setSelectedMinute(parseInt(m));
            onChange?.(val);
          }
        } else {
          onChange?.(val);
        }
      }
    },
    onKeyDown: e => {
      if (e.key === 'Enter' && displayValue.includes(':')) {
        setShowDropdown(false);
      }
    },
    onClick: () => !disabled && setShowDropdown(!showDropdown),
    style: {
      boxSizing: 'border-box',
      margin: 0,
      padding: '4px 11px',
      color: 'rgba(0, 0, 0, 0.88)',
      fontSize: 'inherit',
      lineHeight: 1.57,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
      width: '100%',
      textAlign: 'center',
      backgroundColor: 'transparent',
      border: `1px solid ${showDropdown ? defaultTheme.primaryColor : defaultTheme.borderColor}`,
      borderRadius: '6px',
      outline: 0,
      transition: 'all 0.2s linear',
      cursor: disabled ? 'not-allowed' : 'text',
      opacity: disabled ? 0.6 : 1,
      direction: 'ltr'
    }
  }), showDropdown && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      background: defaultTheme.backgroundColor,
      border: `2px solid ${defaultTheme.primaryColor}`,
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 1000,
      marginTop: '4px',
      maxHeight: '200px',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      height: '200px'
    }
  }, isRange ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto',
      borderRight: `1px solid ${defaultTheme.borderColor}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '4px',
      background: '#f5f5f5',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: 'bold'
    }
  }, "\u0627\u0632 - \u0633\u0627\u0639\u062A"), hours.map(hour => /*#__PURE__*/React.createElement("div", {
    key: hour,
    onClick: () => {
      setStartHour(hour);
    },
    style: {
      padding: '4px 8px',
      cursor: 'pointer',
      background: startHour === hour ? defaultTheme.primaryColor : 'transparent',
      color: startHour === hour ? '#fff' : defaultTheme.textColor,
      textAlign: 'center',
      fontSize: '12px'
    }
  }, persianNumbers ? toPersianDigits$2(String(hour).padStart(2, '0')) : String(hour).padStart(2, '0')))), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto',
      borderRight: `1px solid ${defaultTheme.borderColor}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '4px',
      background: '#f5f5f5',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: 'bold'
    }
  }, "\u0627\u0632 - \u062F\u0642\u06CC\u0642\u0647"), minutes.map(minute => /*#__PURE__*/React.createElement("div", {
    key: minute,
    onClick: () => {
      setStartMinute(minute);
    },
    style: {
      padding: '4px 8px',
      cursor: 'pointer',
      background: startMinute === minute ? defaultTheme.primaryColor : 'transparent',
      color: startMinute === minute ? '#fff' : defaultTheme.textColor,
      textAlign: 'center',
      fontSize: '12px'
    }
  }, persianNumbers ? toPersianDigits$2(String(minute).padStart(2, '0')) : String(minute).padStart(2, '0')))), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto',
      borderRight: `1px solid ${defaultTheme.borderColor}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '4px',
      background: '#f5f5f5',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: 'bold'
    }
  }, "\u062A\u0627 - \u0633\u0627\u0639\u062A"), hours.map(hour => /*#__PURE__*/React.createElement("div", {
    key: hour,
    onClick: () => {
      setEndHour(hour);
    },
    style: {
      padding: '4px 8px',
      cursor: 'pointer',
      background: endHour === hour ? defaultTheme.primaryColor : 'transparent',
      color: endHour === hour ? '#fff' : defaultTheme.textColor,
      textAlign: 'center',
      fontSize: '12px'
    }
  }, persianNumbers ? toPersianDigits$2(String(hour).padStart(2, '0')) : String(hour).padStart(2, '0')))), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '4px',
      background: '#f5f5f5',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: 'bold'
    }
  }, "\u062A\u0627 - \u062F\u0642\u06CC\u0642\u0647"), minutes.map(minute => /*#__PURE__*/React.createElement("div", {
    key: minute,
    onClick: () => {
      setEndMinute(minute);
      const startTime = `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`;
      const endTime = `${String(endHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      onChange?.([startTime, endTime]);
      setShowDropdown(false);
    },
    style: {
      padding: '4px 8px',
      cursor: 'pointer',
      background: endMinute === minute ? defaultTheme.primaryColor : 'transparent',
      color: endMinute === minute ? '#fff' : defaultTheme.textColor,
      textAlign: 'center',
      fontSize: '12px'
    }
  }, persianNumbers ? toPersianDigits$2(String(minute).padStart(2, '0')) : String(minute).padStart(2, '0'))))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto',
      borderRight: `1px solid ${defaultTheme.borderColor}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '4px',
      background: '#f5f5f5',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement("span", null, "\u0633\u0627\u0639\u062A"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => {
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setSelectedHour(now.getHours());
      setSelectedMinute(now.getMinutes());
      onChange?.(currentTime);
      setShowDropdown(false);
    },
    style: {
      padding: '2px 6px',
      fontSize: '9px',
      border: 'none',
      background: defaultTheme.primaryColor,
      color: '#fff',
      borderRadius: '3px',
      cursor: 'pointer'
    }
  }, "\u0627\u0644\u0627\u0646")), hours.map(hour => /*#__PURE__*/React.createElement("div", {
    key: hour,
    onClick: () => handleTimeSelect(hour, selectedMinute),
    style: {
      padding: '4px 8px',
      cursor: 'pointer',
      background: selectedHour === hour ? defaultTheme.primaryColor : 'transparent',
      color: selectedHour === hour ? '#fff' : defaultTheme.textColor,
      textAlign: 'center',
      fontSize: '12px'
    }
  }, persianNumbers ? toPersianDigits$2(String(hour).padStart(2, '0')) : String(hour).padStart(2, '0')))), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '4px',
      background: '#f5f5f5',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: 'bold'
    }
  }, "\u062F\u0642\u06CC\u0642\u0647"), minutes.map(minute => /*#__PURE__*/React.createElement("div", {
    key: minute,
    onClick: () => handleTimeSelect(selectedHour, minute),
    style: {
      padding: '4px 8px',
      cursor: 'pointer',
      background: selectedMinute === minute ? defaultTheme.primaryColor : 'transparent',
      color: selectedMinute === minute ? '#fff' : defaultTheme.textColor,
      textAlign: 'center',
      fontSize: '12px'
    }
  }, persianNumbers ? toPersianDigits$2(String(minute).padStart(2, '0')) : String(minute).padStart(2, '0'))))))));
};

const PERSIAN_DIGITS$1 = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const toPersianDigits$1 = str => str.toString().replace(/\d/g, digit => PERSIAN_DIGITS$1[parseInt(digit)]);
const PersianCalendar = ({
  events = [],
  onEventClick,
  onEventCreate,
  onEventUpdate,
  onEventDelete,
  theme = {},
  initialView = 'month',
  showWeekends = true,
  headerFormat = 'full',
  editable = true,
  disabledHours = [],
  minDate = null,
  maxDate = null,
  enabledDates = null,
  disabledDates = null,
  disabledWeekDays = [],
  showHolidays = true
}) => {
  const defaultTheme = {
    primaryColor: '#1890ff',
    backgroundColor: '#ffffff',
    borderColor: '#e8e8e8',
    textColor: '#000000',
    headerBg: '#fafafa',
    eventRadius: '6px',
    shadow: '0 2px 8px rgba(0,0,0,0.08)',
    ...theme
  };
  const [viewMode, setViewMode] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [displayMonth, setDisplayMonth] = useState({
    year: 1404,
    month: 9
  });
  const [currentDay, setCurrentDay] = useState(1);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [modalClosing, setModalClosing] = useState(false);
  const [holidayModalClosing, setHolidayModalClosing] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    startTime: '',
    endTime: '',
    color: defaultTheme.primaryColor,
    description: '',
    isAllDay: false,
    isRecurring: false,
    recurringType: 'daily',
    recurringEnd: '',
    isMultiDay: false,
    endDate: ''
  });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [showRecurringEndPicker, setShowRecurringEndPicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const persianMonths = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
  const weekDays = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];
  const weekDaysShort = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
  const displayWeekDays = headerFormat === 'short' ? weekDaysShort.reverse() : weekDays.reverse();
  const filteredWeekDays = showWeekends ? displayWeekDays : displayWeekDays.slice(0, 5);
  const gridCols = showWeekends ? 7 : 5;

  // Fetch holidays for current month
  const {
    holidays,
    loading: holidaysLoading
  } = usePersianHolidays(showHolidays ? displayMonth.year : null, showHolidays ? displayMonth.month : null);

  // Combine user events with holidays
  const allEvents = showHolidays ? [...events, ...holidays] : events;
  useEffect(() => {
    const jalali = gregorianToJalali(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate());
    setDisplayMonth({
      year: jalali.jy,
      month: jalali.jm
    });
    setCurrentDay(jalali.jd);

    // Calculate current week based on current day
    const firstDay = getFirstDayOfMonth(jalali.jy, jalali.jm);
    const weekNum = Math.floor((jalali.jd + firstDay - 1) / 7);
    setCurrentWeek(weekNum);
  }, [currentDate]);
  const gregorianToJalali = (gy, gm, gd) => {
    const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    let jy = gy <= 1600 ? 0 : 979;
    gy -= gy <= 1600 ? 621 : 1600;
    let gy2 = gm > 2 ? gy + 1 : gy;
    let days = 365 * gy + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) + Math.floor((gy2 + 399) / 400) - 80 + gd + g_d_m[gm - 1];
    jy += 33 * Math.floor(days / 12053);
    days %= 12053;
    jy += 4 * Math.floor(days / 1461);
    days %= 1461;
    if (days > 365) {
      jy += Math.floor((days - 1) / 365);
      days = (days - 1) % 365;
    }
    const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
    const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
    return {
      jy,
      jm,
      jd
    };
  };
  const jalaliToGregorian = (jy, jm, jd) => {
    let gy = jy <= 979 ? 621 : 1600;
    jy -= jy <= 979 ? 0 : 979;
    let days = 365 * jy + Math.floor(jy / 33) * 8 + Math.floor((jy % 33 + 3) / 4) + 78 + jd + (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
    gy += 400 * Math.floor(days / 146097);
    days %= 146097;
    let leap = true;
    if (days >= 36525) {
      days--;
      gy += 100 * Math.floor(days / 36524);
      days %= 36524;
      if (days >= 365) days++;else leap = false;
    }
    gy += 4 * Math.floor(days / 1461);
    days %= 1461;
    if (days >= 366) {
      leap = false;
      days--;
      gy += Math.floor(days / 365);
      days %= 365;
    }
    const sal_a = [0, 31, leap || gy % 100 !== 0 && gy % 4 === 0 || gy % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let gm = 0;
    for (gm = 0; gm < 13 && days >= sal_a[gm]; gm++) days -= sal_a[gm];
    const gd = days + 1;
    return {
      gy,
      gm,
      gd
    };
  };
  const getDaysInMonth = (year, month) => {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    const isLeap = (year - 979) % 33 % 4 === 1;
    return isLeap ? 30 : 29;
  };
  const getFirstDayOfMonth = (year, month) => {
    const greg = jalaliToGregorian(year, month, 1);
    const date = new Date(greg.gy, greg.gm - 1, greg.gd);
    return (date.getDay() + 1) % 7;
  };
  const getEventsForDate = (year, month, day) => {
    const greg = jalaliToGregorian(year, month, day);
    const dateStr = `${greg.gy}-${String(greg.gm).padStart(2, '0')}-${String(greg.gd).padStart(2, '0')}`;
    return allEvents.filter(e => e.date?.startsWith(dateStr));
  };
  const isDateDisabled = (year, month, day) => {
    const greg = jalaliToGregorian(year, month, day);
    const dateStr = `${greg.gy}-${String(greg.gm).padStart(2, '0')}-${String(greg.gd).padStart(2, '0')}`;
    if (enabledDates && Array.isArray(enabledDates) && enabledDates.length > 0) {
      const isEnabled = enabledDates.some(d => {
        const enabledDate = typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
        return enabledDate === dateStr;
      });
      if (!isEnabled) return true;
    }
    if (disabledDates && Array.isArray(disabledDates) && disabledDates.length > 0) {
      const isDisabled = disabledDates.some(d => {
        const disabledDate = typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
        return disabledDate === dateStr;
      });
      if (isDisabled) return true;
    }
    if (disabledWeekDays && Array.isArray(disabledWeekDays) && disabledWeekDays.length > 0) {
      const jsDate = new Date(greg.gy, greg.gm - 1, greg.gd);
      const weekDay = (jsDate.getDay() + 1) % 7;
      if (disabledWeekDays.includes(weekDay)) return true;
    }
    if (minDate) {
      const minDateObj = new Date(minDate);
      const minDateStr = `${minDateObj.getFullYear()}-${String(minDateObj.getMonth() + 1).padStart(2, '0')}-${String(minDateObj.getDate()).padStart(2, '0')}`;
      if (dateStr < minDateStr) return true;
    }
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      const maxDateStr = `${maxDateObj.getFullYear()}-${String(maxDateObj.getMonth() + 1).padStart(2, '0')}-${String(maxDateObj.getDate()).padStart(2, '0')}`;
      if (dateStr > maxDateStr) return true;
    }
    return false;
  };
  const handleSlotClick = (year, month, day, hour) => {
    if (!editable || isDateDisabled(year, month, day)) return;
    const greg = jalaliToGregorian(year, month, day);
    const dateStr = `${greg.gy}-${String(greg.gm).padStart(2, '0')}-${String(greg.gd).padStart(2, '0')}`;
    setSelectedSlot({
      date: dateStr,
      hour,
      year,
      month,
      day
    });
    setEventForm({
      title: '',
      startTime: `${String(hour).padStart(2, '0')}:00`,
      endTime: `${String(hour + 1).padStart(2, '0')}:00`,
      color: defaultTheme.primaryColor,
      description: '',
      isAllDay: false,
      isRecurring: false,
      recurringType: 'daily',
      recurringEnd: '',
      isMultiDay: false,
      endDate: ''
    });
    setEditingEvent(null);
    setShowEventModal(true);
  };
  const handleEventClick = (event, e) => {
    e?.stopPropagation();
    if (event.isHoliday) {
      setSelectedHoliday(event);
      setShowHolidayModal(true);
      onEventClick?.(event);
      return;
    }
    if (!editable || event.readOnly) {
      onEventClick?.(event);
      return;
    }
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      startTime: event.startTime || event.time || '00:00',
      endTime: event.endTime || '01:00',
      color: event.color || defaultTheme.primaryColor,
      description: event.description || '',
      isAllDay: event.isAllDay || false,
      isRecurring: event.isRecurring || false,
      recurringType: event.recurringType || 'daily',
      recurringEnd: event.recurringEnd || '',
      isMultiDay: event.isMultiDay || false,
      endDate: event.endDate || ''
    });
    setShowEventModal(true);
  };
  const handleSaveEvent = () => {
    if (!eventForm.title.trim()) return;
    const eventData = {
      ...eventForm,
      date: editingEvent?.date || selectedSlot?.date,
      time: eventForm.startTime,
      id: editingEvent?.id || Date.now()
    };
    if (editingEvent) {
      onEventUpdate?.({
        ...editingEvent,
        ...eventData
      });
    } else {
      onEventCreate?.(eventData);
    }
    closeModal();
  };
  const handleDeleteEvent = () => {
    if (editingEvent) {
      onEventDelete?.(editingEvent);
      closeModal();
    }
  };
  const closeModal = () => {
    setModalClosing(true);
    setTimeout(() => {
      setShowEventModal(false);
      setModalClosing(false);
      setEventForm({
        title: '',
        startTime: '',
        endTime: '',
        color: defaultTheme.primaryColor,
        description: '',
        isAllDay: false,
        isRecurring: false,
        recurringType: 'daily',
        recurringEnd: '',
        isMultiDay: false,
        endDate: ''
      });
      setEditingEvent(null);
    }, 200);
  };
  const closeHolidayModal = () => {
    setHolidayModalClosing(true);
    setTimeout(() => {
      setShowHolidayModal(false);
      setHolidayModalClosing(false);
      setSelectedHoliday(null);
    }, 200);
  };
  const getOverlappingEvents = (year, month, day, hour) => {
    const dayEvents = getEventsForDate(year, month, day);
    return dayEvents.filter(e => {
      if (!e.startTime && !e.time) return false;
      const start = e.startTime || e.time;
      const end = e.endTime;
      const startHour = parseInt(start.split(':')[0]);
      const endHour = end ? parseInt(end.split(':')[0]) : startHour + 1;
      return hour >= startHour && hour < endHour;
    });
  };
  const changeMonth = delta => {
    let newMonth = displayMonth.month + delta;
    let newYear = displayMonth.year;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setDisplayMonth({
      year: newYear,
      month: newMonth
    });
  };
  const changeDay = delta => {
    const daysInMonth = getDaysInMonth(displayMonth.year, displayMonth.month);
    let newDay = currentDay + delta;
    let newMonth = displayMonth.month;
    let newYear = displayMonth.year;
    if (newDay > daysInMonth) {
      newDay = 1;
      newMonth++;
      if (newMonth > 12) {
        newMonth = 1;
        newYear++;
      }
    } else if (newDay < 1) {
      newMonth--;
      if (newMonth < 1) {
        newMonth = 12;
        newYear--;
      }
      newDay = getDaysInMonth(newYear, newMonth);
    }
    setCurrentDay(newDay);
    setDisplayMonth({
      year: newYear,
      month: newMonth
    });
  };
  const changeWeek = delta => {
    const newWeek = currentWeek + delta;
    const weeksInMonth = Math.ceil((getDaysInMonth(displayMonth.year, displayMonth.month) + getFirstDayOfMonth(displayMonth.year, displayMonth.month)) / 7);
    if (newWeek >= weeksInMonth) {
      setCurrentWeek(0);
      changeMonth(1);
    } else if (newWeek < 0) {
      changeMonth(-1);
      const prevWeeks = Math.ceil((getDaysInMonth(displayMonth.year, displayMonth.month - 1 || 12) + getFirstDayOfMonth(displayMonth.year, displayMonth.month - 1 || 12)) / 7);
      setCurrentWeek(prevWeeks - 1);
    } else {
      setCurrentWeek(newWeek);
    }
  };
  const getNavigationText = () => {
    if (viewMode === 'day') {
      const greg = jalaliToGregorian(displayMonth.year, displayMonth.month, currentDay);
      const date = new Date(greg.gy, greg.gm - 1, greg.gd);
      const dayOfWeek = (date.getDay() + 1) % 7;
      return `${weekDays[dayOfWeek]} ${toPersianDigits$1(currentDay)} ${persianMonths[displayMonth.month - 1]} ${toPersianDigits$1(displayMonth.year)}`;
    } else if (viewMode === 'week') {
      return `هفته ${toPersianDigits$1(currentWeek + 1)} از ${persianMonths[displayMonth.month - 1]} ${toPersianDigits$1(displayMonth.year)}`;
    }
    return `${persianMonths[displayMonth.month - 1]} ${toPersianDigits$1(displayMonth.year)}`;
  };
  const handleNavigation = delta => {
    if (viewMode === 'day') changeDay(delta);else if (viewMode === 'week') changeWeek(delta);else changeMonth(delta);
  };
  const renderMonthView = () => {
    const daysInMonth = getDaysInMonth(displayMonth.year, displayMonth.month);
    const firstDay = getFirstDayOfMonth(displayMonth.year, displayMonth.month);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      if (!showWeekends && (i === 5 || i === 6)) continue;
      days.push(/*#__PURE__*/React.createElement("div", {
        key: `empty-${i}`,
        style: {
          minHeight: isMobile ? '50px' : '100px',
          border: `1px solid ${defaultTheme.borderColor}`,
          background: '#fafafa',
          transition: 'all 0.2s'
        }
      }));
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const greg = jalaliToGregorian(displayMonth.year, displayMonth.month, day);
      const date = new Date(greg.gy, greg.gm - 1, greg.gd);
      const dayOfWeek = (date.getDay() + 1) % 7;
      if (!showWeekends && (dayOfWeek === 5 || dayOfWeek === 6)) continue;
      const dayEvents = getEventsForDate(displayMonth.year, displayMonth.month, day);
      const isToday = new Date().toDateString() === date.toDateString();
      const isHoliday = dayEvents.some(e => e.isHoliday);
      const isDisabled = isDateDisabled(displayMonth.year, displayMonth.month, day);
      days.push(/*#__PURE__*/React.createElement("div", {
        key: day,
        className: "calendar-day-cell",
        style: {
          minHeight: isMobile ? '50px' : '100px',
          border: `1px solid ${defaultTheme.borderColor}`,
          padding: isMobile ? '2px' : '8px',
          background: isHoliday ? '#fff1f0' : isToday ? `${defaultTheme.primaryColor}08` : defaultTheme.backgroundColor,
          overflow: 'hidden',
          transition: 'all 0.2s',
          cursor: editable && !isDisabled ? 'pointer' : 'default',
          opacity: isDisabled ? 0.5 : 1
        },
        onMouseEnter: e => !isDisabled && (e.currentTarget.style.background = `${defaultTheme.primaryColor}15`),
        onMouseLeave: e => !isDisabled && (e.currentTarget.style.background = isHoliday ? '#fff1f0' : isToday ? `${defaultTheme.primaryColor}08` : defaultTheme.backgroundColor),
        onClick: e => {
          if (e.target === e.currentTarget && !isDisabled) handleSlotClick(displayMonth.year, displayMonth.month, day, 9);
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "calendar-day-number",
        style: {
          fontWeight: 'bold',
          marginBottom: isMobile ? '2px' : '8px',
          color: isHoliday ? '#f5222d' : isToday ? defaultTheme.primaryColor : defaultTheme.textColor,
          fontSize: isMobile ? '10px' : '14px'
        }
      }, day), dayEvents.map((event, i) => /*#__PURE__*/React.createElement("div", {
        key: i,
        onClick: e => handleEventClick(event, e),
        title: `${event.title}${event.description ? ' - ' + event.description : ''}${event.isAllDay ? ' (تمام روز)' : ''}${event.isRecurring ? ' (تکراری)' : ''}`,
        className: "calendar-event",
        style: {
          background: event.isAllDay ? `linear-gradient(45deg, ${event.color || defaultTheme.primaryColor}, ${event.color || defaultTheme.primaryColor}aa)` : event.color || defaultTheme.primaryColor,
          color: '#fff',
          padding: event.isAllDay ? isMobile ? '1px 2px' : '6px 8px' : isMobile ? '1px 2px' : '4px 6px',
          marginBottom: isMobile ? '1px' : '4px',
          borderRadius: isMobile ? '2px' : defaultTheme.eventRadius,
          fontSize: isMobile ? '8px' : '11px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          transition: 'all 0.2s',
          boxShadow: isMobile ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
          border: event.isAllDay ? isMobile ? '1px dashed rgba(255,255,255,0.7)' : '2px dashed rgba(255,255,255,0.5)' : 'none',
          fontWeight: event.isAllDay ? 'bold' : 'normal',
          animation: event.isNew ? 'eventAdd 0.3s ease-out' : event.isDeleting ? 'eventRemove 0.3s ease-out' : 'none',
          lineHeight: isMobile ? '1.2' : '1.4'
        },
        onMouseEnter: e => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
        },
        onMouseLeave: e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        }
      }, isMobile ? event.title.length > 8 ? event.title.substring(0, 8) + '...' : event.title : /*#__PURE__*/React.createElement(React.Fragment, null, event.isAllDay ? /*#__PURE__*/React.createElement("svg", {
        width: "12",
        height: "12",
        viewBox: "0 0 24 24",
        fill: "currentColor",
        style: {
          marginRight: '4px'
        }
      }, /*#__PURE__*/React.createElement("circle", {
        cx: "12",
        cy: "12",
        r: "5"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
      })) : event.time && `${event.time} `, event.title, event.isRecurring ? /*#__PURE__*/React.createElement("svg", {
        width: "12",
        height: "12",
        viewBox: "0 0 24 24",
        fill: "currentColor",
        style: {
          marginLeft: '4px'
        }
      }, /*#__PURE__*/React.createElement("path", {
        d: "M4 12a8 8 0 0 1 8-8V2.5L16 6l-4 3.5V8a6 6 0 1 0 6 6h1.5a7.5 7.5 0 1 1-7.5-7.5z"
      })) : '')))));
    }
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
        gap: '0',
        animation: 'fadeIn 0.3s ease-in-out',
        direction: 'rtl'
      }
    }, days);
  };
  const renderWeekView = () => {
    const firstDay = getFirstDayOfMonth(displayMonth.year, displayMonth.month);
    const daysInMonth = getDaysInMonth(displayMonth.year, displayMonth.month);

    // Calculate the actual week days for current week
    const weekStartDay = Math.max(1, currentWeek * 7 - firstDay + 1);
    const weekEndDay = Math.min(daysInMonth, weekStartDay + 6);
    const hours = Array.from({
      length: 24
    }, (_, i) => i);
    const weekDays = [];
    for (let d = weekEndDay; d >= weekStartDay; d--) {
      weekDays.push(d);
    }
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        flexDirection: 'column'
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: `repeat(${weekDays.length}, 1fr) 60px`,
        borderBottom: `2px solid ${defaultTheme.borderColor}`
      }
    }, weekDays.map(day => {
      const greg = jalaliToGregorian(displayMonth.year, displayMonth.month, day);
      const date = new Date(greg.gy, greg.gm - 1, greg.gd);
      const dayOfWeek = (date.getDay() + 1) % 7;
      return /*#__PURE__*/React.createElement("div", {
        key: day,
        className: "calendar-weekday-header",
        style: {
          padding: isMobile ? '6px 2px' : '8px',
          textAlign: 'center',
          background: defaultTheme.headerBg,
          borderLeft: `1px solid ${defaultTheme.borderColor}`,
          fontSize: isMobile ? '10px' : '14px'
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontWeight: 'bold',
          fontSize: isMobile ? '10px' : '14px'
        }
      }, ['شنبه', 'یکشنبه', 'دوشنبه', 'سه شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'][dayOfWeek]), /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: isMobile ? '9px' : '12px',
          color: '#999'
        }
      }, day));
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        padding: '8px',
        background: defaultTheme.headerBg
      }
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        maxHeight: '600px',
        overflowY: 'auto'
      }
    }, hours.map(hour => /*#__PURE__*/React.createElement("div", {
      key: hour,
      style: {
        display: 'grid',
        gridTemplateColumns: `repeat(${weekDays.length}, 1fr) 60px`,
        borderBottom: `1px solid ${defaultTheme.borderColor}`
      }
    }, weekDays.map(day => {
      const dayEvents = getEventsForDate(displayMonth.year, displayMonth.month, day).filter(e => {
        if (!e.time && !e.startTime) return false;
        const eventHour = parseInt((e.startTime || e.time).split(':')[0]);
        return eventHour === hour;
      });
      const isDisabled = isDateDisabled(displayMonth.year, displayMonth.month, day);
      return /*#__PURE__*/React.createElement("div", {
        key: day,
        style: {
          padding: '4px',
          minHeight: '50px',
          borderRight: `1px solid ${defaultTheme.borderColor}`,
          cursor: editable && !isDisabled ? 'pointer' : 'default',
          opacity: isDisabled ? 0.5 : 1
        },
        onClick: () => !isDisabled && handleSlotClick(displayMonth.year, displayMonth.month, day, hour)
      }, dayEvents.map((event, i) => /*#__PURE__*/React.createElement("div", {
        key: i,
        onClick: e => handleEventClick(event, e),
        title: `${event.title}${event.description ? ' - ' + event.description : ''}${event.isAllDay ? ' (تمام روز)' : ''}${event.isRecurring ? ' (تکراری)' : ''}`,
        style: {
          background: event.isAllDay ? `linear-gradient(45deg, ${event.color || defaultTheme.primaryColor}, ${event.color || defaultTheme.primaryColor}aa)` : event.color || defaultTheme.primaryColor,
          color: '#fff',
          padding: event.isAllDay ? '8px' : '6px',
          marginBottom: '2px',
          borderRadius: defaultTheme.eventRadius,
          fontSize: '11px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: event.isAllDay ? '2px dashed rgba(255,255,255,0.5)' : 'none',
          fontWeight: event.isAllDay ? 'bold' : 'normal'
        },
        onMouseEnter: e => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
        },
        onMouseLeave: e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        }
      }, event.isAllDay ? /*#__PURE__*/React.createElement("svg", {
        width: "12",
        height: "12",
        viewBox: "0 0 24 24",
        fill: "currentColor",
        style: {
          marginRight: '4px'
        }
      }, /*#__PURE__*/React.createElement("circle", {
        cx: "12",
        cy: "12",
        r: "5"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
      })) : '', event.title, event.isRecurring ? /*#__PURE__*/React.createElement("svg", {
        width: "12",
        height: "12",
        viewBox: "0 0 24 24",
        fill: "currentColor",
        style: {
          marginLeft: '4px'
        }
      }, /*#__PURE__*/React.createElement("path", {
        d: "M4 12a8 8 0 0 1 8-8V2.5L16 6l-4 3.5V8a6 6 0 1 0 6 6h1.5a7.5 7.5 0 1 1-7.5-7.5z"
      })) : '')));
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        padding: '8px',
        fontSize: '12px',
        color: '#999',
        textAlign: 'center',
        background: defaultTheme.headerBg
      }
    }, String(hour).padStart(2, '0'), ":00")))));
  };
  const renderDayView = () => {
    const hours = Array.from({
      length: 24
    }, (_, i) => i);
    return /*#__PURE__*/React.createElement("div", {
      style: {
        maxHeight: '600px',
        overflowY: 'auto'
      }
    }, hours.map(hour => {
      const overlappingEvents = getOverlappingEvents(displayMonth.year, displayMonth.month, currentDay, hour);
      const hasOverlap = overlappingEvents.length > 1;
      const isDisabled = disabledHours.includes(hour);
      return /*#__PURE__*/React.createElement("div", {
        key: hour,
        style: {
          display: 'flex',
          borderBottom: `1px solid ${defaultTheme.borderColor}`,
          minHeight: '80px',
          background: isDisabled ? '#f5f5f5' : hasOverlap ? '#fff7e6' : 'transparent',
          opacity: isDisabled ? 0.6 : 1,
          direction: 'rtl'
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          width: '80px',
          padding: '8px',
          fontSize: '14px',
          color: '#999',
          textAlign: 'center',
          borderLeft: `1px solid ${defaultTheme.borderColor}`,
          background: defaultTheme.headerBg
        }
      }, String(hour).padStart(2, '0'), ":00"), /*#__PURE__*/React.createElement("div", {
        style: {
          flex: 1,
          padding: '4px',
          cursor: editable && !isDisabled ? 'pointer' : 'default',
          position: 'relative'
        },
        onClick: () => !isDisabled && handleSlotClick(displayMonth.year, displayMonth.month, currentDay, hour)
      }, isDisabled && /*#__PURE__*/React.createElement("div", {
        style: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#999',
          fontSize: '12px',
          fontWeight: 'bold'
        }
      }, "\u063A\u06CC\u0631\u0641\u0639\u0627\u0644"), hasOverlap && /*#__PURE__*/React.createElement("div", {
        style: {
          position: 'absolute',
          top: '4px',
          right: '4px',
          background: '#fa8c16',
          color: '#fff',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 'bold'
        }
      }, overlappingEvents.length, " \u062A\u062F\u0627\u062E\u0644"), /*#__PURE__*/React.createElement("div", {
        style: {
          display: 'flex',
          gap: '4px',
          flexWrap: 'wrap'
        }
      }, overlappingEvents.map((event, i) => /*#__PURE__*/React.createElement("div", {
        key: i,
        onClick: e => handleEventClick(event, e),
        title: `${event.title}${event.description ? ' - ' + event.description : ''}${event.isAllDay ? ' (تمام روز)' : ''}${event.isRecurring ? ' (تکراری)' : ''}`,
        style: {
          background: event.isAllDay ? `linear-gradient(45deg, ${event.color || defaultTheme.primaryColor}, ${event.color || defaultTheme.primaryColor}aa)` : event.color || defaultTheme.primaryColor,
          color: '#fff',
          padding: '12px',
          borderRadius: defaultTheme.eventRadius,
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: defaultTheme.shadow,
          flex: hasOverlap ? `0 0 calc(${100 / overlappingEvents.length}% - 4px)` : '1',
          minWidth: hasOverlap ? '120px' : 'auto',
          border: event.isHoliday ? 'none' : event.isAllDay ? '3px dashed rgba(255,255,255,0.7)' : '2px solid rgba(255,255,255,0.3)'
        },
        onMouseEnter: e => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
        },
        onMouseLeave: e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = defaultTheme.shadow;
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontWeight: 'bold',
          fontSize: '13px'
        }
      }, event.isAllDay ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("svg", {
        width: "12",
        height: "12",
        viewBox: "0 0 24 24",
        fill: "currentColor",
        style: {
          marginRight: '4px'
        }
      }, /*#__PURE__*/React.createElement("circle", {
        cx: "12",
        cy: "12",
        r: "5"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
      })), "\u062A\u0645\u0627\u0645 \u0631\u0648\u0632") : `${event.startTime || event.time} - ${event.endTime || ''}`), /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: '4px'
        }
      }, event.title, event.isRecurring ? /*#__PURE__*/React.createElement("svg", {
        width: "12",
        height: "12",
        viewBox: "0 0 24 24",
        fill: "currentColor",
        style: {
          marginLeft: '4px'
        }
      }, /*#__PURE__*/React.createElement("path", {
        d: "M4 12a8 8 0 0 1 8-8V2.5L16 6l-4 3.5V8a6 6 0 1 0 6 6h1.5a7.5 7.5 0 1 1-7.5-7.5z"
      })) : ''), event.description && /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: '11px',
          marginTop: '4px',
          opacity: 0.9
        }
      }, event.description))))));
    }));
  };
  const isMobile = window.innerWidth <= 768;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      background: defaultTheme.backgroundColor,
      borderRadius: isMobile ? '8px' : '12px',
      overflow: 'hidden',
      border: `1px solid ${defaultTheme.borderColor}`,
      boxShadow: defaultTheme.shadow,
      position: 'relative',
      width: '100%',
      maxWidth: '100%'
    }
  }, /*#__PURE__*/React.createElement("style", null, `
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes slideOut { from { transform: translateY(0); opacity: 1; } to { transform: translateY(-20px); opacity: 0; } }
        @keyframes eventAdd { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes eventRemove { from { transform: scale(1); opacity: 1; } to { transform: scale(0.8); opacity: 0; } }
        @media (max-width: 768px) {
          .calendar-header { flex-direction: column; gap: 12px !important; }
          .calendar-nav-buttons { order: 2; }
          .calendar-title { order: 1; font-size: 16px !important; }
          .calendar-view-buttons { order: 3; }
          .calendar-grid { font-size: 12px; }
          .calendar-day-cell { min-height: 50px !important; padding: 2px !important; }
          .calendar-event { font-size: 8px !important; padding: 1px 2px !important; margin-bottom: 1px !important; }
          .calendar-day-number { font-size: 10px !important; margin-bottom: 2px !important; }
          .calendar-weekday-header { font-size: 10px !important; padding: 6px 2px !important; }
          .calendar-hour-label { font-size: 11px !important; }
          .calendar-time-slot { min-height: 40px !important; }
        }
      `), /*#__PURE__*/React.createElement("div", {
    className: "calendar-header",
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: isMobile ? '12px' : '20px',
      background: defaultTheme.headerBg,
      borderBottom: `2px solid ${defaultTheme.borderColor}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "calendar-nav-buttons",
    style: {
      display: 'flex',
      gap: isMobile ? '4px' : '8px'
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => handleNavigation(1),
    style: {
      padding: isMobile ? '6px 12px' : '8px 16px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      background: defaultTheme.backgroundColor,
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontWeight: '500',
      fontSize: isMobile ? '12px' : '14px'
    },
    onMouseEnter: e => e.currentTarget.style.background = defaultTheme.primaryColor + '15',
    onMouseLeave: e => e.currentTarget.style.background = defaultTheme.backgroundColor
  }, "\u0628\u0639\u062F\u06CC"), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      const today = new Date();
      const jalali = gregorianToJalali(today.getFullYear(), today.getMonth() + 1, today.getDate());
      setDisplayMonth({
        year: jalali.jy,
        month: jalali.jm
      });
      setCurrentDay(jalali.jd);

      // Calculate current week based on today
      const firstDay = getFirstDayOfMonth(jalali.jy, jalali.jm);
      const weekNum = Math.floor((jalali.jd + firstDay - 1) / 7);
      setCurrentWeek(weekNum);
    },
    style: {
      padding: isMobile ? '6px 12px' : '8px 16px',
      border: `1px solid ${defaultTheme.primaryColor}`,
      borderRadius: '6px',
      background: defaultTheme.primaryColor,
      color: '#fff',
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontWeight: '500',
      fontSize: isMobile ? '12px' : '14px'
    },
    onMouseEnter: e => e.currentTarget.style.opacity = '0.9',
    onMouseLeave: e => e.currentTarget.style.opacity = '1'
  }, "\u0627\u0645\u0631\u0648\u0632"), /*#__PURE__*/React.createElement("button", {
    onClick: () => handleNavigation(-1),
    style: {
      padding: isMobile ? '6px 12px' : '8px 16px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      background: defaultTheme.backgroundColor,
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontWeight: '500',
      fontSize: isMobile ? '12px' : '14px'
    },
    onMouseEnter: e => e.currentTarget.style.background = defaultTheme.primaryColor + '15',
    onMouseLeave: e => e.currentTarget.style.background = defaultTheme.backgroundColor
  }, "\u0642\u0628\u0644\u06CC")), /*#__PURE__*/React.createElement("div", {
    className: "calendar-title",
    style: {
      fontWeight: 'bold',
      fontSize: isMobile ? '16px' : '20px',
      color: defaultTheme.textColor,
      direction: 'rtl',
      textAlign: 'center'
    }
  }, getNavigationText()), /*#__PURE__*/React.createElement("div", {
    className: "calendar-view-buttons",
    style: {
      display: 'flex',
      gap: isMobile ? '2px' : '4px'
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => setViewMode('day'),
    style: {
      padding: isMobile ? '6px 8px' : '8px 16px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      background: viewMode === 'day' ? defaultTheme.primaryColor : defaultTheme.backgroundColor,
      color: viewMode === 'day' ? '#fff' : defaultTheme.textColor,
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontWeight: '500',
      fontSize: isMobile ? '12px' : '14px'
    }
  }, "\u0631\u0648\u0632"), /*#__PURE__*/React.createElement("button", {
    onClick: () => setViewMode('week'),
    style: {
      padding: isMobile ? '6px 8px' : '8px 16px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      background: viewMode === 'week' ? defaultTheme.primaryColor : defaultTheme.backgroundColor,
      color: viewMode === 'week' ? '#fff' : defaultTheme.textColor,
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontWeight: '500',
      fontSize: isMobile ? '12px' : '14px'
    }
  }, "\u0647\u0641\u062A\u0647"), /*#__PURE__*/React.createElement("button", {
    onClick: () => setViewMode('month'),
    style: {
      padding: isMobile ? '6px 8px' : '8px 16px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      background: viewMode === 'month' ? defaultTheme.primaryColor : defaultTheme.backgroundColor,
      color: viewMode === 'month' ? '#fff' : defaultTheme.textColor,
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontWeight: '500',
      fontSize: isMobile ? '12px' : '14px'
    }
  }, "\u0645\u0627\u0647"))), viewMode === 'month' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
      background: defaultTheme.headerBg,
      borderBottom: `2px solid ${defaultTheme.borderColor}`
    }
  }, filteredWeekDays.map((day, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    className: "calendar-weekday-header",
    style: {
      padding: isMobile ? '6px 2px' : '12px',
      textAlign: 'center',
      fontWeight: '600',
      borderLeft: i > 0 ? `1px solid ${defaultTheme.borderColor}` : 'none',
      fontSize: isMobile ? '10px' : '14px'
    }
  }, day))), renderMonthView()), viewMode === 'week' && renderWeekView(), viewMode === 'day' && renderDayView(), showEventModal && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: modalClosing ? 'fadeOut 0.2s' : 'fadeIn 0.2s',
      padding: isMobile ? '16px' : '0'
    },
    onClick: closeModal
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      background: defaultTheme.backgroundColor,
      borderRadius: isMobile ? '8px' : '12px',
      padding: isMobile ? '16px' : '24px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '500px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      animation: modalClosing ? 'slideOut 0.2s' : 'slideIn 0.3s',
      direction: 'rtl',
      maxHeight: isMobile ? '90vh' : 'auto',
      overflowY: isMobile ? 'auto' : 'visible'
    },
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    }
  }, /*#__PURE__*/React.createElement("h3", {
    style: {
      margin: 0,
      fontSize: '20px',
      fontWeight: 'bold'
    }
  }, editingEvent ? 'ویرایش رویداد' : 'رویداد جدید'), /*#__PURE__*/React.createElement("button", {
    onClick: closeModal,
    style: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#999',
      padding: '0',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%'
    },
    onMouseEnter: e => e.currentTarget.style.background = '#f0f0f0',
    onMouseLeave: e => e.currentTarget.style.background = 'none'
  }, "\xD7")), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u0639\u0646\u0648\u0627\u0646"), /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: eventForm.title,
    onChange: e => setEventForm({
      ...eventForm,
      title: e.target.value
    }),
    style: {
      width: '100%',
      padding: '10px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      fontSize: '14px'
    },
    placeholder: "\u0639\u0646\u0648\u0627\u0646 \u0631\u0648\u06CC\u062F\u0627\u062F"
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: eventForm.isAllDay,
    onChange: e => setEventForm({
      ...eventForm,
      isAllDay: e.target.checked
    })
  }), "\u0631\u0648\u06CC\u062F\u0627\u062F \u062A\u0645\u0627\u0645 \u0631\u0648\u0632")), !eventForm.isAllDay && /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u0632\u0645\u0627\u0646 \u0634\u0631\u0648\u0639"), /*#__PURE__*/React.createElement(PersianTimePicker, {
    value: eventForm.startTime,
    onChange: val => setEventForm({
      ...eventForm,
      startTime: val
    }),
    theme: defaultTheme,
    placeholder: "\u0632\u0645\u0627\u0646 \u0634\u0631\u0648\u0639",
    defaultValue: "09:00",
    disabledHours: disabledHours
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u0632\u0645\u0627\u0646 \u067E\u0627\u06CC\u0627\u0646"), /*#__PURE__*/React.createElement(PersianTimePicker, {
    value: eventForm.endTime,
    onChange: val => setEventForm({
      ...eventForm,
      endTime: val
    }),
    theme: defaultTheme,
    placeholder: "\u0632\u0645\u0627\u0646 \u067E\u0627\u06CC\u0627\u0646",
    defaultValue: "10:00",
    disabledHours: disabledHours
  }))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: eventForm.isMultiDay,
    onChange: e => setEventForm({
      ...eventForm,
      isMultiDay: e.target.checked,
      isRecurring: e.target.checked ? false : eventForm.isRecurring
    })
  }), "\u0631\u0648\u06CC\u062F\u0627\u062F \u0686\u0646\u062F \u0631\u0648\u0632\u0647")), eventForm.isMultiDay && /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u062A\u0627\u0631\u06CC\u062E \u067E\u0627\u06CC\u0627\u0646"), /*#__PURE__*/React.createElement(PersianDateTimePicker, {
    value: eventForm.endDate,
    onChange: val => setEventForm({
      ...eventForm,
      endDate: val
    }),
    showTime: false,
    showFooter: false,
    theme: defaultTheme
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: eventForm.isRecurring,
    disabled: eventForm.isMultiDay,
    onChange: e => setEventForm({
      ...eventForm,
      isRecurring: e.target.checked,
      isMultiDay: e.target.checked ? false : eventForm.isMultiDay
    })
  }), "\u0631\u0648\u06CC\u062F\u0627\u062F \u062A\u06A9\u0631\u0627\u0631\u06CC")), eventForm.isRecurring && /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u0646\u0648\u0639 \u062A\u06A9\u0631\u0627\u0631"), /*#__PURE__*/React.createElement("select", {
    value: eventForm.recurringType,
    onChange: e => setEventForm({
      ...eventForm,
      recurringType: e.target.value
    }),
    style: {
      width: '100%',
      padding: '10px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      fontSize: '14px'
    }
  }, /*#__PURE__*/React.createElement("option", {
    value: "daily"
  }, "\u0631\u0648\u0632\u0627\u0646\u0647"), /*#__PURE__*/React.createElement("option", {
    value: "weekly"
  }, "\u0647\u0641\u062A\u06AF\u06CC"), /*#__PURE__*/React.createElement("option", {
    value: "monthly"
  }, "\u0645\u0627\u0647\u0627\u0646\u0647"), /*#__PURE__*/React.createElement("option", {
    value: "yearly"
  }, "\u0633\u0627\u0644\u0627\u0646\u0647"))), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u067E\u0627\u06CC\u0627\u0646 \u062A\u06A9\u0631\u0627\u0631"), /*#__PURE__*/React.createElement(PersianDateTimePicker, {
    value: eventForm.recurringEnd,
    onChange: val => setEventForm({
      ...eventForm,
      recurringEnd: val
    }),
    showTime: false,
    showFooter: false,
    theme: defaultTheme
  }))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u0631\u0646\u06AF"), /*#__PURE__*/React.createElement("input", {
    type: "color",
    value: eventForm.color,
    onChange: e => setEventForm({
      ...eventForm,
      color: e.target.value
    }),
    style: {
      width: '100%',
      height: '40px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      cursor: 'pointer'
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '20px'
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500'
    }
  }, "\u062A\u0648\u0636\u06CC\u062D\u0627\u062A"), /*#__PURE__*/React.createElement("textarea", {
    value: eventForm.description,
    onChange: e => setEventForm({
      ...eventForm,
      description: e.target.value
    }),
    style: {
      width: '100%',
      padding: '10px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      fontSize: '14px',
      minHeight: '80px',
      resize: 'vertical'
    },
    placeholder: "\u062A\u0648\u0636\u06CC\u062D\u0627\u062A \u0631\u0648\u06CC\u062F\u0627\u062F"
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end'
    }
  }, editingEvent && /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleDeleteEvent,
    style: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '6px',
      background: '#ff4d4f',
      color: '#fff',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => e.currentTarget.style.background = '#ff7875',
    onMouseLeave: e => e.currentTarget.style.background = '#ff4d4f'
  }, "\u062D\u0630\u0641"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: closeModal,
    style: {
      padding: '10px 20px',
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: '6px',
      background: defaultTheme.backgroundColor,
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => e.currentTarget.style.background = defaultTheme.hoverColor,
    onMouseLeave: e => e.currentTarget.style.background = defaultTheme.backgroundColor
  }, "\u0644\u063A\u0648"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleSaveEvent,
    style: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '6px',
      background: defaultTheme.primaryColor,
      color: '#fff',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => e.currentTarget.style.opacity = '0.9',
    onMouseLeave: e => e.currentTarget.style.opacity = '1'
  }, "\u0630\u062E\u06CC\u0631\u0647")))), showHolidayModal && selectedHoliday && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: holidayModalClosing ? 'fadeOut 0.2s' : 'fadeIn 0.2s',
      padding: isMobile ? '16px' : '0'
    },
    onClick: closeHolidayModal
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      background: defaultTheme.backgroundColor,
      borderRadius: isMobile ? '8px' : '12px',
      padding: isMobile ? '16px' : '24px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '400px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      animation: holidayModalClosing ? 'slideOut 0.2s' : 'slideIn 0.3s',
      direction: 'rtl'
    },
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    }
  }, /*#__PURE__*/React.createElement("h3", {
    style: {
      margin: 0,
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#ef4444'
    }
  }, "\u062A\u0639\u0637\u06CC\u0644\u0627\u062A \u0631\u0633\u0645\u06CC"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: closeHolidayModal,
    style: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#999',
      padding: '0',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%'
    },
    onMouseEnter: e => e.currentTarget.style.background = '#f0f0f0',
    onMouseLeave: e => e.currentTarget.style.background = 'none'
  }, "\xD7")), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: defaultTheme.textColor,
      marginBottom: '8px'
    }
  }, selectedHoliday.title), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '12px'
    }
  }, selectedHoliday.date)), selectedHoliday.description && /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '20px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: '14px',
      color: '#666',
      lineHeight: '1.6',
      background: '#f9f9f9',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid #e5e5e5'
    }
  }, selectedHoliday.description)), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: closeHolidayModal,
    style: {
      padding: '10px 24px',
      border: 'none',
      borderRadius: '6px',
      background: '#ef4444',
      color: '#fff',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    onMouseEnter: e => e.currentTarget.style.background = '#dc2626',
    onMouseLeave: e => e.currentTarget.style.background = '#ef4444'
  }, "\u0628\u0633\u062A\u0646")))));
};

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const toPersianDigits = str => str.toString().replace(/\d/g, digit => PERSIAN_DIGITS[parseInt(digit)]);
const PersianTimeline = ({
  events = [],
  theme = {},
  persianNumbers = false,
  direction = 'vertical',
  markerShape = 'circular',
  showIcons = true,
  onEventClick,
  alternating = true
}) => {
  const defaultTheme = {
    primaryColor: theme.primaryColor || '#1890ff',
    backgroundColor: theme.backgroundColor || '#ffffff',
    borderColor: theme.borderColor || '#e8e8e8',
    textColor: theme.textColor || '#000000',
    lineColor: theme.lineColor || '#d9d9d9',
    markerSize: theme.markerSize || '32px',
    eventRadius: theme.eventRadius || '12px',
    shadow: theme.shadow || '0 2px 8px rgba(0,0,0,0.08)'
  };
  const gregorianToJalali = (gy, gm, gd) => {
    const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    let jy = gy <= 1600 ? 0 : 979;
    gy -= gy <= 1600 ? 621 : 1600;
    let gy2 = gm > 2 ? gy + 1 : gy;
    let days = 365 * gy + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) + Math.floor((gy2 + 399) / 400) - 80 + gd + g_d_m[gm - 1];
    jy += 33 * Math.floor(days / 12053);
    days %= 12053;
    jy += 4 * Math.floor(days / 1461);
    days %= 1461;
    if (days > 365) {
      jy += Math.floor((days - 1) / 365);
      days = (days - 1) % 365;
    }
    const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
    const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
    return {
      jy,
      jm,
      jd
    };
  };
  const formatPersianDate = dateStr => {
    const date = new Date(dateStr);
    const jalali = gregorianToJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
    const formatted = `${jalali.jy}/${String(jalali.jm).padStart(2, '0')}/${String(jalali.jd).padStart(2, '0')}`;
    return persianNumbers ? toPersianDigits(formatted) : formatted;
  };
  const getEventColor = (event, index) => {
    if (event.color) return event.color;
    const colors = ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    return colors[index % colors.length];
  };
  const renderMarker = (event, index) => {
    const eventColor = getEventColor(event, index);
    const size = defaultTheme.markerSize;
    console.log('Marker size:', size, 'Theme:', defaultTheme);
    const markerStyle = {
      width: size,
      height: size,
      minWidth: size,
      minHeight: size,
      maxWidth: size,
      maxHeight: size,
      borderRadius: markerShape === 'circular' ? '50%' : '6px',
      backgroundColor: eventColor,
      border: `3px solid ${defaultTheme.backgroundColor}`,
      boxShadow: defaultTheme.shadow,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      color: '#fff',
      position: 'relative',
      zIndex: 2,
      flexShrink: 0,
      overflow: 'hidden'
    };
    if (event.image) {
      return /*#__PURE__*/React.createElement("div", {
        style: markerStyle
      }, /*#__PURE__*/React.createElement("img", {
        src: event.image,
        alt: event.title,
        style: {
          width: '100%',
          height: '100%',
          borderRadius: markerShape === 'circular' ? '50%' : '4px',
          objectFit: 'cover'
        }
      }));
    }
    if (showIcons && event.icon) {
      return /*#__PURE__*/React.createElement("div", {
        style: markerStyle
      }, typeof event.icon === 'string' ? /*#__PURE__*/React.createElement("span", null, event.icon) : event.icon);
    }
    return /*#__PURE__*/React.createElement("div", {
      style: markerStyle
    });
  };
  const renderEvent = (event, index) => {
    const isMobile = window.innerWidth <= 768;
    const eventStyle = {
      background: defaultTheme.backgroundColor,
      border: `1px solid ${defaultTheme.borderColor}`,
      borderRadius: defaultTheme.eventRadius,
      padding: isMobile ? '12px 16px' : '18px 22px',
      boxShadow: defaultTheme.shadow,
      cursor: onEventClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      maxWidth: isMobile ? '100%' : '320px',
      minWidth: isMobile ? '100%' : '220px',
      position: 'relative'
    };
    const eventColor = getEventColor(event, index);
    const dateStyle = {
      fontSize: isMobile ? '11px' : '12px',
      color: eventColor,
      fontWeight: 'bold',
      marginBottom: isMobile ? '6px' : '8px'
    };
    const titleStyle = {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: 'bold',
      color: defaultTheme.textColor,
      marginBottom: event.description ? isMobile ? '6px' : '8px' : '0'
    };
    const descriptionStyle = {
      fontSize: isMobile ? '12px' : '14px',
      color: defaultTheme.textColor,
      opacity: 0.7,
      lineHeight: '1.4'
    };
    return /*#__PURE__*/React.createElement("div", {
      style: {
        ...eventStyle,
        borderLeft: `4px solid ${eventColor}`
      },
      onClick: () => onEventClick?.(event),
      onMouseEnter: e => {
        if (onEventClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
        }
      },
      onMouseLeave: e => {
        if (onEventClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = defaultTheme.shadow;
        }
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: dateStyle
    }, formatPersianDate(event.date), event.time && ` - ${persianNumbers ? toPersianDigits(event.time) : event.time}`), /*#__PURE__*/React.createElement("div", {
      style: titleStyle
    }, event.title), event.description && /*#__PURE__*/React.createElement("div", {
      style: descriptionStyle
    }, event.description));
  };
  const isMobile = window.innerWidth <= 768;
  if (direction === 'horizontal') {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: '0',
        padding: isMobile ? '12px' : '20px',
        overflowX: 'auto'
      }
    }, events.map((event, index) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: isMobile ? '10px' : '16px',
        minWidth: isMobile ? '160px' : '220px'
      }
    }, renderEvent(event, index), renderMarker(event, index)), index < events.length - 1 && /*#__PURE__*/React.createElement("div", {
      style: {
        width: isMobile ? '24px' : '48px',
        height: '2px',
        backgroundColor: defaultTheme.lineColor,
        flexShrink: 0
      }
    }))));
  }
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      padding: isMobile ? '12px' : '20px',
      direction: 'rtl'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      right: alternating && !isMobile ? '50%' : isMobile ? '16px' : '24px',
      top: '0',
      bottom: '0',
      width: '2px',
      backgroundColor: defaultTheme.lineColor,
      zIndex: 1
    }
  }), events.map((event, index) => {
    const isLeft = alternating && !isMobile ? index % 2 === 0 : false;
    const useAlternating = alternating && !isMobile;
    return /*#__PURE__*/React.createElement("div", {
      key: index,
      style: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: index < events.length - 1 ? isMobile ? '20px' : '32px' : '0',
        flexDirection: useAlternating ? isLeft ? 'row' : 'row-reverse' : 'row',
        gap: isMobile ? '10px' : '16px'
      }
    }, useAlternating && /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        display: 'flex',
        justifyContent: isLeft ? 'flex-end' : 'flex-start'
      }
    }, isLeft && renderEvent(event, index)), /*#__PURE__*/React.createElement("div", {
      style: {
        position: 'relative',
        zIndex: 2
      }
    }, renderMarker(event, index)), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        display: 'flex',
        justifyContent: useAlternating ? isLeft ? 'flex-start' : 'flex-end' : 'flex-start'
      }
    }, useAlternating ? !isLeft && renderEvent(event, index) : renderEvent(event, index)));
  }));
};

export { PersianCalendar, PersianDateRangePicker, PersianDateTimePicker, PersianTimePicker, PersianTimeline, fetchPersianCalendarEvents, getHolidays, isHoliday, transformEvents, usePersianHolidays };
//# sourceMappingURL=index.esm.js.map
